<template>
  <div class="tw-scope w-full h-screen flex flex-col bg-dark text-white">
    <!-- 顶部导航栏 -->
    <header class="relative z-[100] flex items-center justify-between bg-dark px-4 py-2 shadow-md">
      <div class="text-lg font-bold text-primary">🌉 Wuhan Bridge–Tunnel Network Digital Twin Platform</div>
      <div class="flex space-x-2">
        <button @click="refreshAllObjectColors" class="btn-primary">Refresh Status Colors</button>
        <button @click="resetAllObjectColors" class="btn-secondary">Reset Colors</button>
        <button @click="resetToTopView" class="btn-secondary">Top View</button>
        <button @click="resetToSideView" class="btn-secondary">Axonometric View</button>
        <!-- 图层控制按钮 -->
        <div class="relative">
          <button @click="showLayerMenu = !showLayerMenu" class="btn-secondary">Layer Control</button>
          <div v-if="showLayerMenu" class="absolute right-0 mt-2 bg-gray-800 text-white p-2 rounded shadow-lg z-50 space-y-1">
            <div v-for="(visible, layer) in layerVisibility" :key="layer" class="flex items-center space-x-2">
              <input type="checkbox" v-model="layerVisibility[layer]" @change="updateLayerVisibility" />
              <label>{{ layerDisplayNames[layer] || layer }}</label>
            </div>
          </div>
        </div>
        <!-- 🏠 返回主页按钮 -->
        <button @click="goHome" class="btn-secondary" title="Home">🏠</button>
      </div>
      </header>
      
      <!-- 时间选择面板（主视图区左上角） -->
<div 
 class="absolute top-20 left-[280px] z-50"
>

  <!-- 折叠状态横条 -->
  <div 
  @click="showTimePanel = !showTimePanel"
  class="flex items-center justify-between w-44 px-3 py-1.5
         bg-gray-800/60 backdrop-blur-md 
         hover:bg-gray-700/80 transition-all duration-300
         rounded-lg border border-blue-400/50 
         shadow-md shadow-blue-500/30 cursor-pointer select-none"
>
  <!-- 左侧图标 + 时间 -->
  <div class="flex items-center space-x-2">
    <!-- SVG时钟图标，直接显示，不依赖FontAwesome -->
    <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
    <div class="flex flex-col leading-tight">
      <span class="text-[10px] text-gray-300">{{ selectedDate }}</span>
      <span class="text-xs text-blue-400 font-semibold tracking-wide">{{ formattedTime }}</span>
    </div>
  </div>

  <!-- 右侧箭头 -->
  <svg 
    class="w-3 h-3 text-gray-400 transform transition-transform duration-300"
    :class="showTimePanel ? 'rotate-180' : ''"
    fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"
  >
    <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
</div>

  <!-- 折叠面板内容 -->
  <transition name="fade-slide">
  <div 
    v-if="showTimePanel"
    class="mt-1 w-52 p-3 rounded-lg
             bg-gray-800/70 backdrop-blur-md
             border border-blue-400/40
             shadow-lg shadow-blue-500/30
             space-y-2"
  >
    <!-- 日期选择器 -->
    <label class="block text-xs text-gray-300 mb-1">Select Day</label>
    <select 
      v-model="selectedDate"
      class="w-full px-2 py-1 text-xs rounded-md 
             bg-gray-900/80 border border-gray-600 text-gray-200
             focus:outline-none focus:ring-1 focus:ring-blue-400"
    >
      <option v-for="day in weekDays" :key="day" :value="day">
        {{ day }}
      </option>
    </select>

    <!-- 时间滑块 -->
    <label class="block text-xs text-gray-300 mt-2">Select Time</label>
    <input 
      type="range"
      min="0"
      max="23"
      step="1"
      v-model="selectedHour"
      @input="onSliderInput"
      class="w-full accent-blue-400"
    />
    <div class="text-center text-xs text-blue-400 font-semibold mt-1">
      {{ formattedTime }}
    </div>
  </div>
</transition>
</div>


    <div class="tw-scope flex flex-1 overflow-hidden">
      <!-- 左侧功能栏 -->
      <aside class="w-64 bg-gray-900 p-4 overflow-y-auto">
        <BaseAccordion>
          <BaseAccordionItem title="🚦 Cascading Impact Simulation">
            <template #content>
              <div class=" space-y-2">
              <div class="relative" ref="eventNodeDropdownContainer">
                <input
                  v-model="eventNode"
                  placeholder="Bridge–Tunnel Node ID"
                  class="input-box"
                  @focus="onEventNodeFocus"
                  @blur="onEventNodeBlur"
                  @input="onEventNodeInput"
                  autocomplete="off"
                />
                <ul
                  v-if="eventNodeDropdownVisible && eventNodeFilteredOptions.length > 0"
                  class="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg z-[100]"
                >
                  <li
                    v-for="option in eventNodeFilteredOptions"
                    :key="option.node"
                    @mousedown.prevent="selectEventNodeOption(option)"
                    class="px-3 py-2 cursor-pointer hover:bg-cyan-600 hover:text-white transition-colors text-sm text-gray-200 border-b border-gray-700 last:border-b-0 flex items-center gap-2"
                  >
                    <span class="font-mono text-cyan-400 font-semibold whitespace-nowrap">{{  option.displayNode }}</span>
                    <span class="text-gray-400 truncate">{{ option.name }}</span>
                  </li>
                </ul>
              </div>
                <select v-model.number="eventType" class="input-box">
                  <option :value="1">Inspection</option>
                  <option :value="2">Maintenance</option>
                  <option :value="3">Emergency Control</option>
                </select>
                <button @click="runSimulation" class="btn-primary w-full">Run Simulation</button>
                <button 
                  @click="toggleModel2Visibility" 
                  :class="[model2Visible ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ model2Visible ? 'Hide Animation' : 'Show Animation' }}
                </button>
                <button 
                  @click="toggleFlowSpritesVisibility" 
                  :class="[flowSpritesVisible ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ flowSpritesVisible ? 'Hide Traffic Flow' : 'Show Traffic Flow' }}
                </button>
                <button
                  @click="showSimulationChart = !showSimulationChart"
                  :class="[showSimulationChart ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ showSimulationChart ? 'Hide Simulation Results' : 'Show Simulation Results' }}
                </button>
              </div>
            </template>
          </BaseAccordionItem>
          
          <BaseAccordionItem title="🚧 Traffic Flow Propagation Simulation">
            <template #content>
              <div class="space-y-2">
                <div class="relative" ref="spreadEventNodeDropdownContainer">
                  <input
                    v-model="spreadEventNode"
                    placeholder="Enter Bridge–Tunnel Node ID"
                    class="input-box"
                    @focus="onSpreadEventNodeFocus"
                    @blur="onSpreadEventNodeBlur"
                    @input="onSpreadEventNodeInput"
                    autocomplete="off"
                  />
                  <ul
                    v-if="spreadEventNodeDropdownVisible && spreadEventNodeFilteredOptions.length > 0"
                    class="absolute left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded-md shadow-lg z-[100]"
                  >
                    <li
                      v-for="option in spreadEventNodeFilteredOptions"
                      :key="option.node"
                      @mousedown.prevent="selectSpreadEventNodeOption(option)"
                      class="px-3 py-2 cursor-pointer hover:bg-cyan-600 hover:text-white transition-colors text-sm text-gray-200 border-b border-gray-700 last:border-b-0 flex items-center gap-2"
                    >
                      <span class="font-mono text-cyan-400 font-semibold whitespace-nowrap">{{  option.displayNode }}</span>
                      <span class="text-gray-400 truncate">{{ option.name }}</span>
                    </li>
                  </ul>
                </div>
                <select v-model.number="spreadEventType" class="input-box">
                  <option :value="1">Inspection</option>
                  <option :value="2">Maintenance</option>
                  <option :value="3">Emergency Control</option>
                </select>

                <!-- ✅ 播放速度控制区域 -->
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-400">Playback Speed:</span>
                <div class="flex gap-2">
                  <button @click="spreadSpeed = 0.5" :class="{ 'btn-active': spreadSpeed === 0.5 }" class="btn-mini">0.5x</button>
                  <button @click="spreadSpeed = 1.0" :class="{ 'btn-active': spreadSpeed === 1.0 }" class="btn-mini">1.0x</button>
                  <button @click="spreadSpeed = 3.0" :class="{ 'btn-active': spreadSpeed === 3.0 }" class="btn-mini">3.0x</button>
                </div>
              </div>

                <button 
                  @click="handleSpreadToggle"
                  :class="[isSpreading ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ isSpreading ? 'Stop Propagation Simulation' : 'Run Propagation Simulation' }}
                </button>

                <button
                  v-if="gifUrl"
                  @click="toggleSpreadVisibility"
                  :class="[showGif ? 'btn-secondary' : 'btn-primary', 'w-full']"
                >
                  {{ showGif ? 'Hide Propagation Animation' : 'Show Propagation Animation' }}
                </button>

                <div v-if="showGif" class="pt-2">
                  <img :src="gifUrl" alt="Propagation Animation" class="w-full border border-gray-700 rounded" />
                </div>
              </div>
            </template>
          </BaseAccordionItem>

          <!--
          <BaseAccordionItem title="📈 O&M Statistics">
            <template #content>
              <button
                @click="showPieChart = !showPieChart"
                class="btn-secondary w-full"
              >
                {{ showPieChart ? 'Hide Statistics' : 'View Statistics' }}
              </button>
            </template>
          </BaseAccordionItem>

          <BaseAccordionItem title="🌬️ SHM Data Access">
            <template #content>
              <div class="space-y-2">
                
                <div class="flex items-center justify-between text-xs text-gray-300">
                  <span>Monitoring Type</span>
                  <select v-model="monitorType" class="input-box w-28">
                    <option value="wind">Wind Environment</option>
                    <option value="deflection">Deflection</option>
                  </select>
                </div>

                <div class="flex items-center justify-between text-xs text-gray-300">
                  <span>Refresh Interval (s)</span>
                  <input v-model.number="monitorRefreshSec" type="number" min="1" class="input-box w-20" />
                </div>

                <div class="flex items-center justify-between text-xs text-gray-300">
                 <span>Displayed Points</span>
                 <input v-model.number="monitorLimit" type="number" min="50" max="5000" class="input-box w-20" />
                </div>

                <button @click="toggleAllMonitor" class="btn-primary w-full">
                  {{ monitorRunning || deflectionRunning ? 'Stop Data Refresh' : 'Start Data Refresh' }}
                </button>
              </div>
            </template>
          </BaseAccordionItem>
          -->


          <BaseAccordionItem title="📊 Network Congestion Levels">
            <template #content>
            <div class="flex items-center space-x-1 mb-2">
              <input
                v-model="searchInput"
                type="text"
                class="input-box flex-1"
                placeholder="🔍 Search Node Name..."
                @keyup.enter="performSearch"
              />
              <button 
                @click="performSearch" 
                class="btn-secondary px-2 py-1"
              >
                Search
              </button>
            </div>
            <!-- 展开/折叠按钮 -->
            <button
              @click="showFullRankList = !showFullRankList"
              :class="[ showFullRankList ? 'btn-secondary' : 'btn-primary', 'w-full']"
            >
              {{ showFullRankList ? 'Collapse List ▲' : 'Expand List ▼' }}
            </button>
            
            <!-- 可折叠的 Rank 列表 -->
            <div v-show="showFullRankList">
              <div class="max-h-[360px] overflow-y-auto border border-gray-700 rounded-lg shadow-inner bg-gray-900">
                <table class="w-full text-[12px]">
                  <thead>
                    <tr>
                      <th
                        class="border-b pb-1 text-center w-10 cursor-pointer select-none"
                        @click="sortTableBy('node')"
                      >
                        ID<br>
                        <span v-if="tableSortColumn === 'node'">{{ tableSortOrder === 'asc' ? '▲' : '▼' }}</span>
                        <span v-else class="text-gray-500">⇅</span>
                      </th>
                      <th
                        class="border-b pb-1 text-center cursor-pointer select-none"
                        @click="sortTableBy('name')"
                      >
                        Name<br>
                        <span v-if="tableSortColumn === 'name'">{{ tableSortOrder === 'asc' ? '▲' : '▼' }}</span>
                        <span v-else class="text-gray-500">⇅</span>
                      </th>
                      <th
                        class="border-b pb-1 text-center whitespace-nowrap px-2 w-10 cursor-pointer select-none"
                        @click="sortTableBy('congestion')"
                      >
                        Level<br>
                        <span v-if="tableSortColumn === 'congestion'">{{ tableSortOrder === 'asc' ? '▲' : '▼' }}</span>
                        <span v-else class="text-gray-500">⇅</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-700">
                    <tr
                      v-for="[name, info] in filteredRankMap"
                      :key="info.node"
                      class="hover:bg-gray-800 transition-colors duration-200"
                      @mouseenter="showTooltip(info, $event)"
                      @mouseleave="hideTooltip"
                    >
                      <!-- 编号列 -->
                      <td class="text-center text-[12px] py-1 px-2 font-mono text-gray-300">
                        {{  Number(info.node) + 1  }}
                      </td>

                      <!-- 名称列 -->
                      <td class="text-left text-[12px] py-1 px-2 font-medium cursor-pointer hover:underline"
                          @click="focusOnObject(name)">
                        <span class="inline-block w-3 h-3 rounded-full mr-1 border"
                              :style="{ backgroundColor: rankColors[info.congestion] }"></span>
                        {{ name.replace('对象', '') }}
                      </td>

                      <!-- 当前等级列 -->
                      <td class="text-center text-[12px] py-1 px-2 text-blue-400">
                        {{ info.congestion }}
                      </td>
                    </tr>

                    <!-- 固定悬浮卡片 -->
                    <div v-if="tooltip.visible" 
                        :style="{ top: tooltip.y+'px', left: tooltip.x+'px' }"
                        class="fixed bg-gray-800 text-gray-200 text-xs px-2 py-1 rounded shadow-lg border border-gray-700 z-50 whitespace-nowrap">
                      Node ID: {{ Number(tooltip.node) + 1 }}
                    </div>
                  </tbody>
                </table>
              </div>
            </div>
            </template>
          </BaseAccordionItem>

          <BaseAccordionItem title="🎨 Legend Filter">
            <template #content>
              <div v-for="(color, rank) in rankColors" :key="rank" @click="toggleRankFilter(Number(rank))"
                   class="flex items-center cursor-pointer hover:opacity-80 space-x-2 py-1">
                <span class="w-4 h-4 rounded" :style="{ backgroundColor: color }"></span>
                <span>Level {{ rank }}</span>
              </div>
              <div @click="toggleRankFilter(null)" class="cursor-pointer mt-1 hover:opacity-80">Show All</div>
            </template>
          </BaseAccordionItem>
        </BaseAccordion>
      </aside>

      <!-- 主视图区 -->
<main class="relative flex-1">
  <!-- 1. Three.js 渲染容器 (只在 three 模式显示) -->
  <div v-show="viewMode === 'three'" ref="threeCanvas" class="absolute inset-0"></div>

  <!-- 2. 地图 iframe (只在 map 模式显示) -->
<iframe
  ref="mapIframe"
  v-show="viewMode === 'map'"
  :srcdoc="mapHtml"
  class="absolute inset-0 w-full h-full border-0"
  loading="lazy"
  @load="onMapIframeLoad"
></iframe>

  <!-- 3. 模型加载动画 -->
  <div v-if="modelLoading" class="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-40 z-50">
    <div class="loader-ring"></div>
    <p class="text-white mt-4 text-lg font-semibold tracking-wide">Loading 3D Model...</p>
  </div>

  <!-- 4. 右上角视图切换按钮 -->
  <div class="absolute top-4 right-4 z-50 flex gap-2">
    <button
      @click="viewMode = 'three'"
      :class="viewMode === 'three' ? 'bg-cyan-500' : 'bg-gray-700'"
      class="px-3 py-1 rounded text-white hover:bg-gray-600 transition"
    >
      3D Model
    </button>
    <button
      @click="viewMode = 'map'"
      :class="viewMode === 'map' ? 'bg-cyan-500' : 'bg-gray-700'"
      class="px-3 py-1 rounded text-white hover:bg-gray-600 transition"
    >
      Network Topology
    </button>
  </div>

  <!-- 5. 推演结果图表（独立于视图，始终置顶） -->
  <div
    v-show="showSimulationChart"
    ref="simulateBarChart"
    class="absolute bottom-6 right-6 w-[600px] h-[400px] rounded-lg p-4 shadow-lg z-40"
    style="background-color: #1e1e2f;"
  ></div>

  <!-- 6. 其他仅模型视图才显示的覆盖层（饼图、调试面板） -->
  <div v-show="viewMode === 'three'">
    <!-- 原有的饼图 -->
    <div v-show="showPieChart" ref="pieChartContainer" class="absolute top-6 right-6 w-[600px] h-[400px] rounded-lg p-4 shadow-lg" style="background-color: rgba(192,192,192,1);"></div>
    <!-- 原有的调试面板 -->
    <div ref="debugPanel" v-if="debugObject" class="absolute z-50 bg-black bg-opacity-70 p-4 rounded shadow-lg text-sm space-y-1" :style="{ top: debugPanelPosition.y + 'px', left: debugPanelPosition.x + 'px' }" @mousedown.prevent="onPanelMouseDown">
      <div><strong>Name:</strong> {{ selectedNodeInfo.name }}</div>
      <div><strong>Node ID:</strong> {{ Number(selectedNodeInfo.node) + 1 }}</div>
      <div><strong>Congestion Level:</strong> {{ selectedNodeInfo.congestion }}</div>
      <div><strong>Speed:</strong> {{ selectedNodeInfo.speed }} km/h</div>
      <div><strong>Traffic Flow:</strong> {{ selectedNodeInfo.flow }} veh/h</div>
      <button class="btn-secondary w-full mt-2" @click="toggleObjectsByNodeVisibility">
        {{ selectedObject && selectedObject.userData.node && objectVisibilityMap[selectedObject.userData.node] === false ? 'Show Object' : 'Hide Object' }}
      </button>
    </div>
  </div>
</main>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import * as echarts from 'echarts';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
//import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
//import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { modelLoader } from '@/utils/ModelLoader.js';//预加载模型
//import LineChart from './LineChart.vue';
import api from '@/utils/api';
import axios from 'axios';
import gsap from 'gsap';
import mapHtml from '@/assets/map_wuhan.html?raw';

import BaseAccordion from './BaseAccordion.vue';
import BaseAccordionItem from './BaseAccordionItem.vue';
//import { FALSE } from 'node-sass';
import '@/assets/main.css';

export default {
  name: 'ThreeJSComponent',
  components: { BaseAccordion, BaseAccordionItem },
  data() {
      const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const now = new Date();
      const getDayToIndex = [6, 0, 1, 2, 3, 4, 5];
      const selectedDate = weekDays[getDayToIndex[now.getDay()]];
      const selectedHour = now.getHours();
      return {
        mapIframeReady: false,
pendingMapMessages: [], 
            viewMode: 'three',  // 控制当前视图：'three' 表示 3D模型，'map' 表示网络拓扑
    mapHtml: mapHtml,
        chartData: [
          { product: 'Inspections', '2020': 56.5, '2021': 82.1, '2022': 88.7, '2023': 70.1, '2024': 53.4, '2025': 8 },
          { product: 'Maintenance Activities', '2020': 51.1, '2021': 51.4, '2022': 55.1, '2023': 53.3, '2024': 73.8, '2025': 6 },
          { product: 'Traffic Incidents', '2020': 40.1, '2021': 62.2, '2022': 69.5, '2023': 36.4, '2024': 45.2, '2025': 3 },
          { product: 'Emergency Control', '2020': 25.2, '2021': 37.1, '2022': 41.2, '2023': 18, '2024': 33.9, '2025': 4 }
        ],
        bridgeFlowNameMap: {
            '武汉长江大桥动画': '武汉长江大桥',
            '武汉长江二桥动画': '武汉长江二桥',
            '杨泗港长江大桥动画': '杨泗港长江大桥',
            '阳逻大桥动画': '阳逻长江大桥',
            '白沙洲大桥动画': '白沙洲长江大桥',
            '二七长江大桥动画': '二七长江大桥',
            '青山动画': '青山长江大桥',
            '鹦鹉洲动画': '鹦鹉洲长江大桥',
            '天兴洲动画': '天兴洲长江大桥',
        },
        modelLoading: true,
        tableSortColumn: null,
        tableSortOrder: 'asc',
        searchInput: '',     // 输入框绑定的临时值
        model2: null,            // 保存 model2 的引用
        model2Visible: false,    // 动画模型当前是否可见
        flowSpritesVisible: false, // 流量精灵当前是否可见
        vehicleMeshes: [],         // 存储所有车辆网格
        speedThreshold: 500.0,      // 速度阈值（世界单位/秒）
        animationSpeed: 0.1,   // 默认正常速度
        yangpuBridgeFlow: null,
        bridgeMixers: [],
        bridgeDefaultSpeeds: {},
        lastTime: 0,
        // ===== SHM 监测 =====
        monitorRunning: false,
        monitorTimer: null,
        monitorRefreshSec: 5,
        monitorLimit: 600,
        showMonitorChart: false,
        monitorChartInstance: null,
        monitorLatest: [],

        // ✅ 挠度监测
        deflectionRunning: false,
        deflectionTimer: null,
        deflectionRefreshSec: 2,
        deflectionLimit: 600,
        showDeflectionChart: false,
        deflectionChartInstance: null,
        deflectionLatest: [],

        monitorType: "wind",

        scene: null,
        camera: null,
        isAnimating: true,
        renderer: null,
        raycaster: new THREE.Raycaster(),
        mouse: new THREE.Vector2(),
        controls: null,
        selectedObject: null,
        // 调试面板显示用的响应式数据
        selectedNodeInfo: {
          name: '—',
          node: '—',
          congestion: '—',
          speed: '—',
          flow: '—',
        },
        // 最新节点数据索引：node -> 最新交通数据
        latestNodeDataMap: new Map(),
        hoverItem: null,
        debugObject: null,//控制调试面板
        editRank: null,
        isRankLoading:false,
        showFullRankList:false,
        currentObjectColorHex: '#ffffff',
        tooltip: { visible: false, node: null, x: 0, y: 0 },
        selectedRankFilter: null, // 当前筛选 rank（null 表示显示全部）
        showRankPanel: false,
        globalRankMap: {}, // { name: rank }
        searchKeyword: '',     // 搜索关键字
        eventNode: '198',
        eventType: 1,
        showTimePanel: false,
        selectedDate,
        selectedHour,
        debounceTimer: null,
        weekDays,
        simulationResult: [],
        sortOrder: 'desc', // 默认降序
        isLoadingSimulation: false,
        isDraggingPanel: false,
        dragOffset: { x: 0, y: 0 },
        debugPanelPosition: { x: 100, y: 100 }, // 默认位置
        cameraLastPos: new THREE.Vector3(),
        hideExcluded: false,
        groundOriginalY: -20, 
        showSimulationChart: false,//控制模拟结果显示
        showPieChart: false,
        objectVisibilityMap: {},  // key: node 值，value: true/false 表示是否隐藏
        blinkingInterval: null,
        blinkingTimeout: null,
        isSpreading: false, // 控制是否正在传播
        spreadAbortController: null, // 控制取消传播（可选增强）
        spreadEventNode: '',
        spreadEventType: 1,
        spreadSpeed: 1.0, // 默认播放速度为正常速度
        showGif: false,
        spreadResultSequence: [],
        gifUrl: '',
        layerVisibility: {
          桥梁: true,
          隧道: true,
          建筑: true,
          路网: true,
          水系: true,
          用地: true
        },
        layerDisplayNames: {
          桥梁: 'Bridges',
          隧道: 'Tunnels',
          建筑: 'Buildings',
          路网: 'Road Network',
          水系: 'Water Bodies',
          用地: 'Land Use'
        },
        showLayerMenu: false,
        layerKeywordMap: {
          桥梁: ['桥', 'Bridge'],
          隧道: ['隧', 'Tunnel'],
          建筑: ['building', '楼','Areasbuildingroof','Object','Sphere011_Material002_0'],
          路网: ['road', 'highway', '路','Wayshighway','motorway'],
          水系: ['water', '水','外环内主要水系'],
          用地: ['landuse', '用地','Areasleisure','Areaslanduse','公园','农田','商业区','居民区','工业区','森林','树林','湿地','花园','草地','绿地']
        },
      rankColors: {
        4: '#ff0000',
        3: '#ffa500',
        2: '#ffff00',
        1: '#00ff00',
        0: '#1e90ff'
      },
        selectedObjectInfo: '',
        simplifiedObjects: [], // 存储简化后的对象
        excludedNames: ['?','水','公园','农田','商业区','居民区','工业区','森林','树林','湿地','花园','草地','绿地','motorway','外环内主要水系', 'Areasleisure','Areaslanduse','Object_25','Sphere011_Material002_0','Areasbuilding','Object_3001','Areasbuildingroof','Object_2001','Wayshighway','Object_2','Object_8','Object_24','Object_23'],  // 添加要排除交互的mesh名称
        allNodesFlowData: [],
        flowSprites: [],
        targetSpriteScreenHeight: 20, // 期望的屏幕像素高度
        eventNodeDropdownVisible: false,
        eventNodeFilteredOptions: [],
        spreadEventNodeDropdownVisible: false,
        spreadEventNodeFilteredOptions: [],
        allNodeOptions: [], // 缓存所有节点选项 [{ node, name }]
      };
    },
    computed: {
      
      // 显示格式化时间
      formattedTime() {
      const hour = Math.floor(this.selectedHour);
      const minute = this.selectedHour % 1 ? '30' : '00'; // 半小时显示
      return `${hour.toString().padStart(2, '0')}:${minute}`;
    },
      
      filteredRankMap() {
        const keyword = this.searchKeyword?.toLowerCase() || "";
        let entries = Object.entries(this.globalRankMap || {});

        // 搜索过滤
        if (keyword) {
          entries = entries.filter(([name]) =>
            name.toLowerCase().includes(keyword)
          );
        }

        // 根据 tableSortColumn 排序
        if (this.tableSortColumn) {
          entries.sort((a, b) => {
            let valA, valB;
            if (this.tableSortColumn === 'node') {
              valA = a[1].node;
              valB = b[1].node;
            } else if (this.tableSortColumn === 'name') {
              valA = a[0];
              valB = b[0];
            } else if (this.tableSortColumn === 'congestion') {
              valA = a[1].congestion;
              valB = b[1].congestion;
            }
            // 字符串排序
            if (typeof valA === 'string') {
              return this.tableSortOrder === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
            } else {
              // 数字排序
              return this.tableSortOrder === 'asc' ? valA - valB : valB - valA;
            }
          });
        }

        return entries;
      },
      targetWeekday() {
        return this.weekDays.indexOf(this.selectedDate) + 1;  // 星期一 => 1
      },
      targetHour() {
        // 直接返回滑块小时值，Python 现在接收的是小时
        return this.selectedHour;
      },

    sortedSimulationResult() {
      const sorted = [...this.simulationResult].sort((a, b) => {
        return this.sortOrder === 'desc'
          ? b['Traffic Flow'] - a['Traffic Flow']
          : a['Traffic Flow'] - b['Traffic Flow'];
      });
      return sorted;
    }
    
  },
  created() {
  // 缓存节流版检查函数
  this.checkCameraMoveThrottled = this.throttle(this.checkCameraMovement, 300);
},

  methods: {
    // ---------- 地图 iframe 通信 ----------
onMapIframeLoad() {
  const iframeWin = this.$refs.mapIframe.contentWindow;

  const checkMap = setInterval(() => {
    for (const key in iframeWin) {
      try {
        const obj = iframeWin[key];
        if (obj && obj._container && obj._layers) {
          iframeWin.map = obj;
          if (!iframeWin.L) console.warn('Leaflet 未在 iframe 中找到');
          clearInterval(checkMap);

          iframeWin._spreadCircles = iframeWin._spreadCircles || [];
          iframeWin.removeEventListener('message', iframeWin._spreadMsgHandler);
          
          iframeWin._spreadMsgHandler = (event) => {
            const { type, nodeId } = event.data || {};
            if (type === 'spread-step') {
              const coords = iframeWin.nodeCoords[String(nodeId)];
              if (coords) {
                const baseRadius = 6;
                const peakRadius = 10;
                const circle = new iframeWin.L.circleMarker(coords, {
                  radius: baseRadius,
                  fillColor: '#ff0000',
                  color: 'transparent',
                  weight: 0,
                  fillOpacity: 0.6,
                  zIndexOffset: 10000
                }).addTo(iframeWin.map);
                iframeWin._spreadCircles.push(circle);

                let pulseCount = 0;
                const maxPulses = 3;
                const pulseInterval = 300;
                const pulseIn = () => {
                  if (pulseCount >= maxPulses) {
                    circle.setRadius(baseRadius);
                    return;
                  }
                  circle.setRadius(peakRadius);
                  setTimeout(() => {
                    circle.setRadius(baseRadius);
                    pulseCount++;
                    setTimeout(pulseIn, pulseInterval / 2);
                  }, pulseInterval / 2);
                };
                pulseIn();
              } else {
                console.warn('❌ 找不到节点坐标:', nodeId);
              }
            } else if (type === 'spread-clear') {
              iframeWin._spreadCircles.forEach(c => iframeWin.map.removeLayer(c));
              iframeWin._spreadCircles = [];
            }
          };
          iframeWin.addEventListener('message', iframeWin._spreadMsgHandler);

          this.mapIframeReady = true;
          this.pendingMapMessages.forEach(msg => this.sendToMap(msg));
          this.pendingMapMessages = [];
          console.log('✅ 武汉拓扑地图已就绪');
          break;
        }
      } catch (e) {
        // 忽略访问属性错误
      }
    }
  }, 200);
},

sendToMap(message) {
  if (this.mapIframeReady && this.$refs.mapIframe) {
    this.$refs.mapIframe.contentWindow.postMessage(message, '*');
  } else {
    this.pendingMapMessages.push(message);
  }
},
// ✅ 新增：从 globalRankMap 构建所有节点选项（去重）
buildAllNodeOptions() {
  const map = this.globalRankMap || {};
  const seenNodes = new Set();
  const options = [];
  for (const [name, info] of Object.entries(map)) {
    const node = info.node;
    if (node != null && !seenNodes.has(String(node))) {
      seenNodes.add(String(node));
      options.push({
        node: String(node),              // 原始编号（内部使用）
        displayNode: String(Number(node) + 1),  // 显示编号（前端展示）
        name: name
      });
    }
  }
  // 按编号数值排序
  options.sort((a, b) => {
    const numA = parseInt(a.node, 10);
    const numB = parseInt(b.node, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.node.localeCompare(b.node);
  });
  this.allNodeOptions = options;
},

// ✅ 新增：从输入值提取数字前缀用于过滤
extractNumericPrefix(value) {
  const match = String(value || '').match(/^(\d+)/);
  return match ? match[1] : '';
},

// ✅ 新增：从输入值提取纯节点编号（用于提交给后端）
extractNodeId(value) {
  const match = String(value || '').match(/^(\d+)/);
  if (match) {
    const displayNum = parseInt(match[1], 10);
    // 减 1 得到原始编号，并确保不为负数
    const originalNum = Math.max(displayNum - 1, 0);
    return String(originalNum);
  }
  return String(value);  // 非数字开头则原样返回（后面逻辑可能报错，保持兼容）
},

// ✅ 智能过滤：数字前缀匹配 + 汉字包含匹配
smartFilter(value) {
  const input = String(value || '').trim();
  if (!input) return [];
  
  const startsWithDigit = /^\d/.test(input);
  
  if (startsWithDigit) {
    const prefix = (input.match(/^(\d+)/) || [])[1] || input;
    // 只匹配显示编号的前缀
    return this.allNodeOptions.filter(opt =>
      String(opt.displayNode).startsWith(prefix)
    );
  } else {
    const keyword = input.toLowerCase();
    return this.allNodeOptions.filter(opt =>
      opt.name.toLowerCase().includes(keyword)
    );
  }
},

// ===== 级联推演输入框事件处理 =====
onEventNodeFocus() {
  this.eventNodeFilteredOptions = this.smartFilter(this.eventNode);
  this.eventNodeDropdownVisible = this.eventNodeFilteredOptions.length > 0;
},

onEventNodeBlur() {
  setTimeout(() => {
    this.eventNodeDropdownVisible = false;
  }, 150);
},

onEventNodeInput() {
  this.eventNodeFilteredOptions = this.smartFilter(this.eventNode);
  this.eventNodeDropdownVisible = this.eventNodeFilteredOptions.length > 0;
},

selectEventNodeOption(option) {
  this.eventNode = `${option.displayNode} ${option.name}`;
  this.eventNodeDropdownVisible = false;
},

// ===== 传播推演输入框事件处理 =====
onSpreadEventNodeFocus() {
  this.spreadEventNodeFilteredOptions = this.smartFilter(this.spreadEventNode);
  this.spreadEventNodeDropdownVisible = this.spreadEventNodeFilteredOptions.length > 0;
},

onSpreadEventNodeBlur() {
  setTimeout(() => {
    this.spreadEventNodeDropdownVisible = false;
  }, 150);
},

onSpreadEventNodeInput() {
  this.spreadEventNodeFilteredOptions = this.smartFilter(this.spreadEventNode);
  this.spreadEventNodeDropdownVisible = this.spreadEventNodeFilteredOptions.length > 0;
},

selectSpreadEventNodeOption(option) {
  this.spreadEventNode = `${option.displayNode} ${option.name}`;
  this.spreadEventNodeDropdownVisible = false;
},

// ✅ 新增：点击页面其他地方关闭下拉
onGlobalClick(e) {
  const eventContainer = this.$refs.eventNodeDropdownContainer;
  if (eventContainer && !eventContainer.contains(e.target)) {
    this.eventNodeDropdownVisible = false;
  }
  const spreadContainer = this.$refs.spreadEventNodeDropdownContainer;
  if (spreadContainer && !spreadContainer.contains(e.target)) {
    this.spreadEventNodeDropdownVisible = false;
  }
},
    
            sortTableBy(column) {
              //paixv排序函数
              if (this.tableSortColumn === column) {
                this.tableSortOrder = this.tableSortOrder === 'asc' ? 'desc' : 'asc';
              } else {
                this.tableSortColumn = column;
                this.tableSortOrder = 'asc';
              }
            },

        handleMiddleClick(event) {
          // 中键处理
          if (event.button !== 1) return;
          event.preventDefault();  // 阻止浏览器默认的滚动行为

          const canvas = this.$refs.threeCanvas;
          if (!canvas) return;

          const rect = canvas.getBoundingClientRect();
          const mouse = new THREE.Vector2();
          mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
          mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

          const raycaster = new THREE.Raycaster();
          raycaster.setFromCamera(mouse, this.camera);

          // 定义 y = 0 平面（法向量向上）
          const planeY0 = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
          const intersection = new THREE.Vector3();

          if (raycaster.ray.intersectPlane(planeY0, intersection)) {
            // 记录当前相机相对于目标的偏移
            const offset = this.camera.position.clone().sub(this.controls.target);

            // 使用 gsap 平滑移动目标点，并保持相机相对位置不变
            gsap.to(this.controls.target, {
              x: intersection.x,
              y: intersection.y,
              z: intersection.z,
              duration: 1.0,
              ease: 'power2.out',
              onUpdate: () => {
                this.camera.position.copy(this.controls.target).add(offset);
                this.controls.update();
              }
            });
          }
        },
  updateLatestNodeDataMap(latestData) {
      if (!Array.isArray(latestData)) {
        console.warn('⚠️ latestData 不是数组，无法更新 latestNodeDataMap');
        this.latestNodeDataMap = new Map();
        return;
      }

      this.latestNodeDataMap = new Map(
        latestData.map(item => [Number(item.node), item])
      );

      console.log('✅ latestNodeDataMap 已更新，节点数量:', this.latestNodeDataMap.size);
    },
        updateDebugPanelBySelectedObject(obj = this.selectedObject) {
      if (!obj) {
        this.selectedNodeInfo = {
          name: '—',
          node: '—',
          congestion: '—',
          speed: '—',
          flow: '—',
        };
        return;
      }

      const node = Number(obj.userData?.node);

      if (!Number.isFinite(node)) {
        this.selectedNodeInfo = {
          name: obj.name || '—',
          node: '—',
          congestion: obj.userData?.congestion ?? '—',
          speed: obj.userData?.speed ?? '—',
          flow: obj.userData?.flow ?? '—',
        };

        console.warn('⚠️ 当前对象没有有效 node:', obj.name);
        return;
      }

      const latestItem = this.latestNodeDataMap.get(node);

      this.selectedNodeInfo = {
        name: obj.name || '—',
        node: node,
        congestion: latestItem?.congestion ?? obj.userData?.congestion ?? '—',
        speed: latestItem?.speed ?? obj.userData?.speed ?? '—',
        flow: latestItem?.flow ?? obj.userData?.flow ?? '—',
      };

      console.log('✅ 调试面板已刷新:', this.selectedNodeInfo);
    },
    //屏蔽功能
            initVehicleDetection(model) {
          const vehicles = [];
          model.traverse((child) => {
            if (child.isMesh && child.name && child.name.toLowerCase().includes('car')) {
              // 记录上一帧位置（世界坐标）
              child.userData.lastPosition = child.getWorldPosition(new THREE.Vector3());
              vehicles.push(child);
              console.log(`🚗 检测到车辆: ${child.name}`);
            }
          });
          this.vehicleMeshes = vehicles;
          console.log(`✅ 共检测到 ${vehicles.length} 辆车辆，速度阈值=${this.speedThreshold} 单位/秒`);
        },

        updateVehicleVisibility(deltaTime) {
          if (this.vehicleMeshes.length === 0) return;
          if (deltaTime > 0.1) return; // 防止卡顿时delta过大导致误判

          const tempVec = new THREE.Vector3(); // 临时向量避免频繁创建
          this.vehicleMeshes.forEach(vehicle => {
            const currentPos = vehicle.getWorldPosition(tempVec);
            const lastPos = vehicle.userData.lastPosition;
            const distance = currentPos.distanceTo(lastPos);
            const speed = distance / deltaTime;

            if (speed > this.speedThreshold) {
              if (vehicle.visible) vehicle.visible = false;
            } else {
              if (!vehicle.visible) vehicle.visible = true;
            }
            vehicle.userData.lastPosition.copy(currentPos);
          });
        },
        // 切换 model2 的显示/隐藏
        toggleModel2Visibility() {
          if (!this.model2) return;
          this.model2.visible = !this.model2.visible;
          this.model2Visible = this.model2.visible;

          if (this.model2Visible) {
            // 显示动画 -> 获取最新流量，更新速度和可见性
            this.updateBridgeAnimationsByFlow();
          } else {
            // 隐藏动画 -> 暂停所有 mixer
            this.bridgeMixers.forEach(({ mixer }) => {
              const action = mixer._actions[0];
              if (action) action.timeScale = 0;
            });
          }
        },
        performSearch() {
          this.searchKeyword = this.searchInput;
            this.showFullRankList = true; 
        },
        // 切换流量柱和数字精灵
        async toggleFlowSpritesVisibility() {
          if (!this.flowSpritesVisible) {
            await this.addNumbersToAllNodes();
            this.flowSpritesVisible = true;
          } else {
            this.clearAllFlowSprites();
            this.flowSpritesVisible = false;
          }
        },

        //设置动画速度
        async updateBridgeAnimationsByFlow() {
          // 获取最新的全部节点流量数据
          const flowData = await this.fetchAllNodesFlow();
          if (!flowData || flowData.length === 0) return;
console.log('当前 bridgeMixers 名称：', this.bridgeMixers.map(m => m.name));
console.log('bridgeFlowNameMap keys：', Object.keys(this.bridgeFlowNameMap));
          this.bridgeMixers.forEach(({ name, mixer, root }) => {
            // 找到映射后的流量名称
            const flowName = this.bridgeFlowNameMap[name];
            if (!flowName) return;

            // 从流量数组中查找对应数据
            const flowItem = flowData.find(d => d.name === flowName);
            const flow = flowItem ? flowItem.flow : 0;
            const speed = flowItem ? flowItem.speed : 0;


            const action = mixer._actions[0];
            if (action) {
              const speedScale = Math.min(0.06, Math.max(0.01, 0.01+0.000625*speed)); 
              action.timeScale = speedScale;
              this.bridgeDefaultSpeeds[name] = speedScale;  
            }

            // 流量为 0 则隐藏该桥的车辆动画
            root.visible = flow > 0;

            console.log(`🌉 ${name} 流量=${flow}, 速度=${root.visible ? action.timeScale : 0}x`);
          });
        },


        // ===== 新增：更新数字精灵大小，使其屏幕尺寸固定 =====
    updateSpritesScreenSize() {
      if (!this.camera || !this.renderer || !this.flowSprites.length) return;

      const fov = this.camera.fov * Math.PI / 180; // 弧度
      const height = this.renderer.domElement.clientHeight;
      if (height === 0) return;

      const tanHalfFov = Math.tan(fov / 2);
      const targetPixels = this.targetSpriteScreenHeight;
      const aspectRatio = 600 / 150; // 宽高比 (与创建时一致)

      const cameraPos = this.camera.position;
      const worldPos = new THREE.Vector3(); // 临时复用

      this.flowSprites.forEach(item => {
        const sprite = item.sprite;
        if (!sprite) return;

        // 获取精灵世界位置
        sprite.getWorldPosition(worldPos);
        const distance = worldPos.distanceTo(cameraPos);

        // 计算期望的世界高度
        const targetHeightWorld = targetPixels * (2 * distance * tanHalfFov) / height;
        const targetWidthWorld = targetHeightWorld * aspectRatio;

        // 应用缩放（保持 z 为 1，因为是平面精灵）
        sprite.scale.set(targetWidthWorld, targetHeightWorld, 1);
      });
    },

    // 根据流量值获取颜色
    getColorByFlow(flowValue) {
    if (flowValue < 1000) {
      return { 
        hex: 0x003CF6,        // 明亮海蓝（起始色）
        css: '#003CF6',
        name: 'brightblue'
      };
    } else if (flowValue < 2000) {
      return { 
        hex: 0x002BB8,        // 中蓝
        css: '#002BB8',
        name: 'mediumblue'
      };
    } else {
      return { 
        hex: 0x001A7A,        // 深海蓝
        css: '#001A7A',
        name: 'deepblue'
      };
    }
  },

    // 创建柱子函数（基于流量值计算高度）
    createPillar(flowValue) {
      // 计算柱子高度：最小高度150，最大高度1500，按流量比例缩放
      const minHeight = 150;
      const maxHeight = 1500;
      
      // 找到所有流量中的最大值用于比例计算
      const maxFlow = Math.max(...this.allNodesFlowData.map(d => d.flow), 1);
      
      // 计算柱子高度：流量越大柱子越高
      const pillarHeight = minHeight + (flowValue / maxFlow) * (maxHeight - minHeight);
      
      // 柱子半径固定为15
      const pillarRadius = 40;
      
      // 创建圆柱体几何
      const pillarGeometry = new THREE.CylinderGeometry(
        pillarRadius, // 顶部半径
        pillarRadius, // 底部半径
        pillarHeight, // 高度
        16           // 分段数
      );
      
      // 根据流量值获取颜色
      const flowColor = this.getColorByFlow(flowValue);
      
      // 创建材质 - 根据流量值使用不同颜色
      const pillarMaterial = new THREE.MeshBasicMaterial({
        color: flowColor.hex, // 根据流量值设置颜色
        transparent: true,
        opacity: 0.9
      });
      
      // 创建柱子网格
      const pillarMesh = new THREE.Mesh(pillarGeometry, pillarMaterial);
      
      // 设置柱子的位置：底部在原点
      pillarMesh.position.y = pillarHeight / 2; // 将柱子向上移动一半高度，使底部在原点
      
      return { pillarMesh, pillarHeight, flowColor };
    },

    // 创建数字精灵和柱子组
    createNumberSpriteWithPillar(position, flowData) {
      const flowText = flowData.formattedFlow || flowData.flow.toFixed(0);
      const flowValue = flowData.flow;
      
      // 根据流量值获取颜色
      const flowColor = this.getColorByFlow(flowValue);
      
      // ✅ 提高分辨率以提高清晰度
      const resolutionMultiplier = 4; // 提高4倍分辨率
      
      // 创建canvas来绘制文本
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // 设置canvas尺寸 - 提高分辨率
      canvas.width = 1536 * resolutionMultiplier / 3; // 调整尺寸
      canvas.height = 384 * resolutionMultiplier / 3; // 调整尺寸
      
      // 清除画布
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      // 设置文本样式 - 按比例增加字体大小
      const fontSize = 360 * resolutionMultiplier / 3; // 调整字体大小
      context.font = `bold ${fontSize}px Arial`;
      context.fillStyle = flowColor.css; // 根据流量值设置文字颜色
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.strokeStyle = '#ffffff'; // 白色描边
      context.lineWidth = 0; // 调整描边宽度
      
      // 绘制文本（先描边再填充，使文字更清晰）
      context.strokeText(flowText, canvas.width / 2, canvas.height / 2);
      context.fillText(flowText, canvas.width / 2, canvas.height / 2);
      
      // 创建纹理
      const texture = new THREE.CanvasTexture(canvas);
      
      // ✅ 优化纹理设置以提高清晰度
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      texture.premultiplyAlpha = false;
      
      // 创建精灵材质
      const spriteMaterial = new THREE.SpriteMaterial({ 
        map: texture,
        transparent: true,
        opacity: 1.0
      });
      
      // 创建精灵
      const sprite = new THREE.Sprite(spriteMaterial);
      
      // 设置精灵大小（固定大小）
      const spriteWidth = 600;
      const spriteHeight = 150;
      sprite.scale.set(spriteWidth, spriteHeight, 1);
      
      // 创建柱子
      const { pillarMesh, pillarHeight } = this.createPillar(flowValue);
      
      // 创建一个组来包含标签和柱子
      const labelGroup = new THREE.Group();
      
      // 将柱子和标签添加到组中
      labelGroup.add(pillarMesh);
      labelGroup.add(sprite);
      
      // 调整数字标签位置，使其在柱子顶部
      // 柱子高度是pillarHeight，标签在柱子顶部上方30个单位
      sprite.position.y = pillarHeight + 30;
      
      // 设置组的位置
      labelGroup.position.copy(position);
      labelGroup.position.y += 5; // 在模型上方200个单位
      
      return { 
        group: labelGroup, 
        sprite: sprite, 
        pillar: pillarMesh,
        node: flowData.node,
        name: flowData.name,
        flow: flowText,
        flowValue: flowValue,
        flowColor: flowColor
      };
    },
    
    // 从后端获取所有节点的flow数据
    async fetchAllNodesFlow() {
      try {
        // 调用新的接口获取所有节点的流量数据
        const response = await axios.get('/api/whflow/all-nodes-flow');
        
        if (response.data.success) {
          this.allNodesFlowData = response.data.data;

          //this.animationSpeed = this.yangpuBridgeFlow/1000;
          //console.log('动画速度:', this.animationSpeed);
          
          console.log('✅ 获取所有节点flow数据成功:', {
            total: response.data.total,
            latestDate: response.data.latestDate
          });
          
          return this.allNodesFlowData;
        } else {
          console.warn('⚠️ 获取所有节点流量数据失败:', response.data.message);
          return [];
        }
      } catch (err) {
        console.error('❌ 获取所有节点数据失败:', err);
        return [];
      }
    },
    
    // 为所有节点添加数字精灵和柱子
async addNumbersToAllNodes() {
    const flowData = await this.fetchAllNodesFlow();
    if (flowData.length === 0) {
        console.log('没有找到节点流量数据');
        return;
    }
    this.clearAllFlowSprites();

    // ✅ 新增：记录已创建柱子的水平坐标 (x, z)
    const createdPositions = new Set();
    let addedCount = 0;

    for (const nodeData of flowData) {
        const nodeName = nodeData.name;
        let targetObject = null;
        let targetPosition = null;

        this.scene.traverse((child) => {
            if (child.isMesh && child.name && child.name.includes(nodeName)) {
                targetObject = child;
                const worldPosition = new THREE.Vector3();
                child.getWorldPosition(worldPosition);
                targetPosition = worldPosition;
            }
        });

        if (targetObject && targetPosition) {
            // ✅ 新增：生成水平坐标的唯一键（保留两位小数避免浮点误差）
            const posKey = `${Math.round(targetPosition.x * 100) / 100},${Math.round(targetPosition.z * 100) / 100}`;

            // ✅ 新增：如果该水平位置已创建过柱子，跳过
            if (createdPositions.has(posKey)) {
                console.log(`⏭️ 跳过重复水平位置 (${posKey})，节点 ${nodeName}`);
                continue;
            }

            // 原有过滤条件：原点附近不创建
            const EPSILON = 0.01;
            if (Math.abs(targetPosition.x) < EPSILON && Math.abs(targetPosition.z) < EPSILON) {
                console.log(`🚫 跳过原点附近节点 ${nodeName} (node=${nodeData.node})`);
                continue;
            }

            // ✅ 新增：将当前位置登记到 Set 中
            createdPositions.add(posKey);

            // 创建柱子和数字精灵
            const flowSprite = this.createNumberSpriteWithPillar(targetPosition, nodeData);
            this.scene.add(flowSprite.group);
            this.flowSprites.push(flowSprite);
            addedCount++;
        }
    }
    console.log(`🎯 总计为 ${addedCount} 个节点添加了流量数字和柱子（已过滤原点附近及重复水平位置）`);
},
    
    // 清除所有流量数字精灵和柱子
    clearAllFlowSprites() {
      if (this.flowSprites.length === 0) return;
      
      this.flowSprites.forEach(flowSprite => {
        if (flowSprite.group) {
          this.scene.remove(flowSprite.group);
          
          // 释放材质和几何体资源
          if (flowSprite.sprite && flowSprite.sprite.material) {
            flowSprite.sprite.material.dispose();
          }
          if (flowSprite.pillar) {
            if (flowSprite.pillar.geometry) flowSprite.pillar.geometry.dispose();
            if (flowSprite.pillar.material) flowSprite.pillar.material.dispose();
          }
        }
      });
      
      this.flowSprites = [];
      console.log('✅ 已清除所有流量数字精灵和柱子');
    },
    
    // 更新所有节点的数字和柱子
    async updateAllNodesNumbers() {
      // 清除旧的数字精灵和柱子
      this.clearAllFlowSprites();
      
      // 重新创建数字精灵和柱子
      await this.addNumbersToAllNodes();
    },
    
    toggleAllMonitor() {
  const running = this.monitorRunning || this.deflectionRunning;

  if (running) {
    // 停止全部
    this.monitorRunning = false;
    this.deflectionRunning = false;

    if (this.monitorTimer) clearInterval(this.monitorTimer);
    if (this.deflectionTimer) clearInterval(this.deflectionTimer);

    this.monitorTimer = null;
    this.deflectionTimer = null;

    this.showMonitorChart = false;
    this.showDeflectionChart = false;
    return;
  }

  // 启动全部
  this.monitorRunning = true;
  this.deflectionRunning = true;

  this.refreshMonitorOnce();
  this.refreshDeflectionOnce();

  this.monitorTimer = setInterval(
    () => this.refreshMonitorOnce(),
    this.monitorRefreshSec * 1000
  );

  this.deflectionTimer = setInterval(
    () => this.refreshDeflectionOnce(),
    this.deflectionRefreshSec * 1000
  );
},

    async fetchDeflectionLatest() {
  try {
    const res = await axios.get(
      `/api/monitor/deflection/latest?limit=${this.deflectionLimit}`
    );

    console.group("📐 /api/monitor/deflection/latest response");
    console.log(res.data);
    console.groupEnd();

    if (res.data?.success && Array.isArray(res.data.data)) {
      this.deflectionLatest = res.data.data
        .map(r => {
          const date = new Date(r.t);
    return {
      t: date,
      tLabel: date.toISOString(),

      d_1_16_up: Number(r.col1), // 1/16 上游
      d_1_8_up:  Number(r.col2), // 1/8 上游
      d_1_8_dn:  Number(r.col3), // 1/8 下游
      d_3_16_dn: Number(r.col4), // 3/16 下游
    };
  })
  .filter(r =>
    !isNaN(r.t.getTime()) &&
    [r.d_1_16_up, r.d_1_8_up, r.d_1_8_dn, r.d_3_16_dn]
      .every(v => Number.isFinite(v))
  )
  .sort((a, b) => a.t - b.t);

      return this.deflectionLatest;
    }

    this.deflectionLatest = [];
    return [];
  } catch (e) {
    console.error("❌ fetchDeflectionLatest error:", e);
    this.deflectionLatest = [];
    return [];
  }
},


renderDeflectionChart() {
  const dom = this.$refs.deflectionChart;
  if (!dom || dom.clientWidth === 0 || dom.clientHeight === 0) return;

  if (!this.deflectionChartInstance) {
    this.deflectionChartInstance = echarts.init(dom);
  }

  const rows = this.deflectionLatest || [];
  if (!rows.length) return;

  /* ========= 时间轴 ========= */
  const x = rows.map(r => r.tLabel);

  /* ========= 挠度原始值 (mm) ========= */
  const s1 = rows.map(r => r.d_1_16_up);
  const s2 = rows.map(r => r.d_1_8_up);
  const s3 = rows.map(r => r.d_1_8_dn);
  const s4 = rows.map(r => r.d_3_16_dn);

  /* ========= Δ 挠度 (μm)，裁剪浮点误差 ========= */
  const delta = arr => {
    const base = arr[0];
    return arr.map(v =>
      Number(((v - base) * 1000).toFixed(3))
    );
  };

  const d1 = delta(s1);
  const d2 = delta(s2);
  const d3 = delta(s3);
  const d4 = delta(s4);

  /* ========= 绝对值 y 轴范围（mm） ========= */
  const absAll = [...s1, ...s2, ...s3, ...s4].filter(Number.isFinite);
  const rawMin = Math.min(...absAll);
  const rawMax = Math.max(...absAll);

  const step = 0.001; // 1 μm
  const quantizeDown = v => Math.floor(v / step) * step;
  const quantizeUp   = v => Math.ceil(v / step) * step;

  const rawPad = (rawMax - rawMin) * 0.2 || step * 5;
  const pad = quantizeUp(rawPad);

  const absMin = quantizeDown(rawMin - pad);
  const absMax = quantizeUp(rawMax + pad);

  /* ========= Δ 挠度 y 轴范围（μm） ========= */
  const dAll = [...d1, ...d2, ...d3, ...d4];
  const dMax = Math.max(...dAll.map(v => Math.abs(v))) || 1;
  const dRange = Math.ceil(dMax / 5) * 5;

  const axisCommon = {
    axisLine: { lineStyle: { color: "#aaa" } },
    axisLabel: { color: "#ddd", fontSize: 10 },
    splitLine: { lineStyle: { color: "rgba(255,255,255,0.08)" } }
  };

  const option = {
    backgroundColor: "transparent",

    title: {
      text: "Deflection Monitoring — Yangsigang Yangtze River Bridge",
      left: "center",
      top: 8,
      textStyle: { color: "#fff", fontSize: 13, fontWeight: "bold" }
    },

    legend: {
      top: 34,
      left: "center",
      textStyle: { color: "#ddd", fontSize: 10 },
      data: ["1/16 Span (Upstream)", "1/8 Span (Upstream)", "1/8 Span (Downstream)", "3/16 Span (Downstream)"]
    },

    grid: [
      { left: 90, right: 25, top: 80, height: "32%" },
      { left: 90, right: 25, top: "60%", height: "26%" }
    ],

    dataZoom: [
      { type: "inside", xAxisIndex: [0, 1] },
      { type: "slider", xAxisIndex: [0, 1], height: 14, bottom: 6 }
    ],

    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      backgroundColor: "rgba(50,50,70,0.9)",
      textStyle: { color: "#fff" }
    },

    xAxis: [
      {
        type: "category",
        data: x,
        gridIndex: 0,
        ...axisCommon,
        axisLabel: { show: false }
      },
      {
        type: "category",
        data: x,
        gridIndex: 1,
        ...axisCommon,
        axisLabel: {
          rotate: 30,
          formatter: v => v.slice(11, 19)
        }
      }
    ],

    yAxis: [
      {
        name: "Absolute Deflection (mm)",
        nameLocation: "middle",
        nameGap: 60,
        nameTextStyle: {
          color: "#fff",
          fontWeight: "bold",
          fontSize: 12
        },
        type: "value",
        gridIndex: 0,
        min: absMin,
        max: absMax,
        axisLabel: {
          formatter: v => Number(v).toFixed(3)
        },
        ...axisCommon
      },
      {
        name: "Deflection Change, Δ (μm)",
        nameLocation: "middle",
        nameGap: 60,
        nameTextStyle: {
          color: "#fff",
          fontWeight: "bold",
          fontSize: 12
        },
        type: "value",
        gridIndex: 1,
        min: -dRange,
        max:  dRange,
        axisLabel: {
          formatter: v => v.toFixed(1)
        },
        ...axisCommon
      }
    ],

    series: [
      { name: "1/16 Span (Upstream)", type: "line", data: s1, xAxisIndex: 0, yAxisIndex: 0, smooth: true, showSymbol: false },
      { name: "1/8 Span (Upstream)",  type: "line", data: s2, xAxisIndex: 0, yAxisIndex: 0, smooth: true, showSymbol: false },
      { name: "1/8 Span (Downstream)",  type: "line", data: s3, xAxisIndex: 0, yAxisIndex: 0, smooth: true, showSymbol: false },
      { name: "3/16 Span (Downstream)", type: "line", data: s4, xAxisIndex: 0, yAxisIndex: 0, smooth: true, showSymbol: false },

      { name: "1/16 Span (Upstream)", type: "line", data: d1, xAxisIndex: 1, yAxisIndex: 1, smooth: true, showSymbol: false },
      { name: "1/8 Span (Upstream)",  type: "line", data: d2, xAxisIndex: 1, yAxisIndex: 1, smooth: true, showSymbol: false },
      { name: "1/8 Span (Downstream)",  type: "line", data: d3, xAxisIndex: 1, yAxisIndex: 1, smooth: true, showSymbol: false },
      { name: "3/16 Span (Downstream)", type: "line", data: d4, xAxisIndex: 1, yAxisIndex: 1, smooth: true, showSymbol: false }
    ]
  };

  this.deflectionChartInstance.setOption(option, true);
}


,

async refreshDeflectionOnce() {
  await this.fetchDeflectionLatest();
  this.showDeflectionChart = true;
  this.$nextTick(() => this.renderDeflectionChart());
},

toggleDeflectionMonitor() {
  if (this.deflectionRunning) {
    this.deflectionRunning = false;
    this.showDeflectionChart = false;
    if (this.deflectionTimer) clearInterval(this.deflectionTimer);
    this.deflectionTimer = null;
    return;
  }

  this.deflectionRunning = true;
  this.refreshDeflectionOnce();

  if (this.deflectionTimer) clearInterval(this.deflectionTimer);
  this.deflectionTimer = setInterval(() => {
    if (!this.deflectionRunning) return;
    this.refreshDeflectionOnce();
  }, Math.max(1000, this.deflectionRefreshSec * 1000));
},


    async fetchMonitorLatest() {
  try {
    const res = await axios.get(`/api/monitor/latest?limit=${this.monitorLimit}`);

    console.group("📡 /api/monitor/latest response");
    console.log(res.data);
    console.groupEnd();

    if (res.data && res.data.success && Array.isArray(res.data.data)) {

      this.monitorLatest = res.data.data
        .map(r => {
          // ⭐ 1. 统一时间来源
          const timeStr = r.t ?? r.col0;
          const date = new Date(timeStr);

          return {
            // ⭐ 2. 使用 Date 对象（用于排序 & time 轴）
            t: date,

            // ⭐ 3. 单独存一个 label 给 category 轴用
            tLabel: date.toISOString(),

            wind_speed: Number(r.col1),
            wind_direction: Number(r.col2),
            wind_attack: Number(r.col3),
            temperature: Number(r.col4),
            humidity: Number(r.col5),
          };
        })
        // ⭐ 4. 过滤非法数据
        .filter(r =>
          !isNaN(r.t.getTime()) &&
          Number.isFinite(r.wind_speed) &&
          Number.isFinite(r.wind_direction) &&
          Number.isFinite(r.wind_attack) &&
          Number.isFinite(r.temperature) &&
          Number.isFinite(r.humidity)
        )
        // ⭐⭐⭐ 5. 核心：按真实时间排序
        .sort((a, b) => a.t - b.t);

      console.log(
        "✅ monitorLatest sorted:",
        this.monitorLatest[0]?.t,
        "→",
        this.monitorLatest[this.monitorLatest.length - 1]?.t
      );

      return this.monitorLatest;
    }

    this.monitorLatest = [];
    return [];
  } catch (e) {
    console.error("❌ fetchMonitorLatest error:", e);
    this.monitorLatest = [];
    return [];
  }
},


renderMonitorChart() {
  const dom = this.$refs.monitorChart;
  if (!dom || dom.clientWidth === 0 || dom.clientHeight === 0) return;

  if (!this.monitorChartInstance) {
    this.monitorChartInstance = echarts.init(dom);
  }

  const rows = this.monitorLatest || [];
  if (!rows.length) return;

  const x = rows.map(r => r.tLabel);

  const axisCommon = {
    axisLine: { lineStyle: { color: "#aaa" } },
    axisLabel: { color: "#ddd", fontSize: 10 },
    splitLine: { lineStyle: { color: "rgba(255,255,255,0.08)" } }
  };

  const option = {
    backgroundColor: "transparent",

    title: {
      text: "Wind Monitoring — Yangsigang Yangtze River Bridge",
      left: "center",
      top: 6,
      textStyle: { color: "#fff", fontSize: 13, fontWeight: "bold" }
    },

    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "line",
        lineStyle: { color: "#aaa", width: 1 }
      },
      backgroundColor: "rgba(50,50,70,0.9)",
      borderColor: "#666",
      textStyle: { color: "#fff" }
    },

    legend: {
      top: 34,
      left: "center",
      textStyle: { color: "#ddd", fontSize: 10 },
      data: [
        "Wind Speed (m/s)",
        "Wind Direction (°)",
        "Wind Attack Angle (°)",
        "Temperature (°C)",
        "Relative Humidity (%)"
      ]
    },

    /* =====================================================
     * ⭐ 核心修正：grid 垂直间距彻底拉开
     * ===================================================== */
    grid: [
  /* ===============================
   * 第一张：风速
   * =============================== */
  {
    left: 90,
    right: 25,
    top: "20%",
    height: "25%"
  },

  /* ===============================
   * 第二张：风向 + 风攻角
   * （⬅️ 重点：只比第一张下移一个“安全 gap”）
   * =============================== */
  {
    left: 90,
    right: 25,
    top: "45%",   // ← 原来 38% 太小、66% 太大，这里是关键值
    height: "20%"
  },

  /* ===============================
   * 第三张：温度 / 湿度
   * （⬅️ 收紧与第二张的距离）
   * =============================== */
  {
    left: 90,
    right: 25,
    top: "68%",
    height: "20%"
  }
]
,

    xAxis: [
      {
        type: "category",
        data: x,
        gridIndex: 0,
        ...axisCommon,
        axisLabel: { show: false },
        axisTick: { show: false }
      },
      {
        type: "category",
        data: x,
        gridIndex: 1,
        ...axisCommon,
        axisLabel: { show: false },
        axisTick: { show: false }
      },
      {
        type: "category",
        data: x,
        gridIndex: 2,
        ...axisCommon,
        axisLabel: {
          show: true,
          rotate: 30,
          interval: "auto",
          formatter: v => v.slice(11, 19)
        }
      }
    ],

    /* =====================================================
     * ⭐ yAxis：进一步防重叠
     * ===================================================== */
    yAxis: [
      {
        name: "Wind Speed (m/s)",
        nameLocation: "middle",
        nameGap: 50,           // ⬅️ 明显增大
        nameTextStyle: {
          color: "#fff",
          fontWeight: "bold",
          fontSize: 11
        },
        type: "value",
        gridIndex: 0,
        ...axisCommon
      },
      {
        name: "Angle (°)",
        nameLocation: "middle",
        nameGap: 50,
        nameTextStyle: {
          color: "#fff",
          fontWeight: "bold",
          fontSize: 11
        },
        type: "value",
        min: 0,
        max: 360,
        gridIndex: 1,
        ...axisCommon
      },
      {
        name: "Temperature (°C) / Relative Humidity (%)",
        nameLocation: "middle",
        nameGap: 50,
        nameTextStyle: {
          color: "#fff",
          fontWeight: "bold",
          fontSize: 11
        },
        type: "value",
        gridIndex: 2,
        ...axisCommon
      }
    ],

    series: [
      {
        name: "Wind Speed (m/s)",
        type: "line",
        data: rows.map(r => r.wind_speed),
        xAxisIndex: 0,
        yAxisIndex: 0,
        showSymbol: false,
        smooth: true
      },
      {
        name: "Wind Direction (°)",
        type: "line",
        data: rows.map(r => r.wind_direction),
        xAxisIndex: 1,
        yAxisIndex: 1,
        showSymbol: false
      },
      {
        name: "Wind Attack Angle (°)",
        type: "line",
        data: rows.map(r => r.wind_attack),
        xAxisIndex: 1,
        yAxisIndex: 1,
        showSymbol: false
      },
      {
        name: "Temperature (°C)",
        type: "line",
        data: rows.map(r => r.temperature),
        xAxisIndex: 2,
        yAxisIndex: 2,
        showSymbol: false
      },
      {
        name: "Relative Humidity (%)",
        type: "line",
        data: rows.map(r => r.humidity),
        xAxisIndex: 2,
        yAxisIndex: 2,
        showSymbol: false
      }
    ]
  };

  this.monitorChartInstance.setOption(option, true);
}
,


async refreshMonitorOnce() {
      const data = await this.fetchMonitorLatest();
      if (!Array.isArray(data) || data.length === 0) {
        this.showMonitorChart = false;
        return;
      }

      this.showMonitorChart = true;
      await this.$nextTick();
      this.renderMonitorChart();
    },

toggleMonitor() {
  if (this.monitorRunning) {
    // stop
    this.monitorRunning = false;
    this.showMonitorChart = false;
    if (this.monitorTimer) clearInterval(this.monitorTimer);
    this.monitorTimer = null;
    return;
  }

  // start
  this.monitorRunning = true;
  this.refreshMonitorOnce();

  if (this.monitorTimer) clearInterval(this.monitorTimer);
  this.monitorTimer = setInterval(() => {
    if (!this.monitorRunning) return;
    this.refreshMonitorOnce();
  }, Math.max(1000, this.monitorRefreshSec * 1000));
},


    toggleSpreadVisibility() {
    this.showGif = !this.showGif;

    // 控制 Three.js 中对象可见性
    const visible = this.showGif;
    this._activeSprites?.forEach(sprite => sprite.visible = visible);
    this._activeRings?.forEach(ring => ring.visible = visible);
    this._activeSpheres?.forEach(sphere => sphere.visible = visible); // 若你也有 sphere 动画对象
  },
  
    showTooltip(info, evt) {
    this.tooltip = {
      visible: true,
      node: info.node,
      x: evt.clientX + 10, // 鼠标右下角
      y: evt.clientY + 10,
    };
  },
  hideTooltip() {
    this.tooltip.visible = false;
  },
    updateTCurrent() {
    const segment = Math.floor(this.selectedHour / 2);
    this.tCurrent = Math.min(segment + 1, 12);
    console.log('当前全局 tCurrent 更新为:', this.tCurrent);
  },
onSliderInput() {
  // 去掉 updateTCurrent
  clearTimeout(this.debounceTimer);
  this.debounceTimer = setTimeout(() => {
    this.runSimulation();  // 滑动结束后自动触发推演
  }, 500);
},
    findObjectByNodeId(nodeId) {
  let result = null;
  this.scene.traverse(obj => {
    if (obj.userData?.node === Number(nodeId)) {
      result = obj;
    }
  });
  return result;
},

async playSpreadAnimation(spreadSequence) {
  if (!spreadSequence || spreadSequence.length === 0) {
    console.warn('⚠️ 传播序列为空，跳过播放');
    return;
  }

  this._shouldStopSpread = false;
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  this._activeSprites = new Map();
  this._activeRings = new Map();
  this._activeSpheres = new Map();

  this.resetToTopView();

  // ✅ 启动 sprite 动画（仅一次，带脉冲计数和自动停止）
  if (!this._spriteAnimationStarted) {
    this._spriteAnimationActive = true;
    const animateSprites = () => {
      requestAnimationFrame(animateSprites);
      if (!this._spriteAnimationActive) return;

      const time = performance.now() * 0.005;
      if (!this._activeSprites) return;

      this._activeSprites.forEach((sprite, nodeId) => {
        if (sprite.userData.done) return; // 已完成的不再变化

        const base = 80;
        const amplitude = 20;
        const s = base + amplitude * Math.sin(time + nodeId);

        const isMax = s > 98;
        if (isMax && !sprite.userData.lastWasMax) {
          sprite.userData.pulseCount = (sprite.userData.pulseCount || 0) + 1;
          sprite.userData.lastWasMax = true;
        } else if (!isMax) {
          sprite.userData.lastWasMax = false;
        }

        if (sprite.userData.pulseCount >= 3) {
          sprite.scale.set(100, 100, 1);
          sprite.material.opacity = 1.0;
          sprite.userData.done = true;
        } else {
          sprite.scale.set(s, s, 1);
          sprite.material.opacity = 0.6 + 0.4 * Math.sin(time + nodeId);
        }
      });
    };
    this._spriteAnimationStarted = true;
    animateSprites();
  }

  const filteredSequence = spreadSequence.filter(step => {
    const node = step.node;
    const object = this.findObjectByNodeId(node);
    if (!object) {
      console.warn(`❌ [预过滤] 未找到 node=${node} 的模型对象`);
      return false;
    }
    const worldPos = new THREE.Vector3();
    object.getWorldPosition(worldPos);
    const isNearOrigin = worldPos.length() < 1e-2;
    const isTooHigh = worldPos.y > 50;
    if (isNearOrigin) console.warn(`⚠️ [预过滤] node=${node} 位置≈原点，疑似未绑定模型`);
    if (isTooHigh) console.warn(`⚠️ [预过滤] node=${node} 高度 y=${worldPos.y.toFixed(2)} > 50，跳过`);
    return !isNearOrigin && !isTooHigh;
  });

  if (filteredSequence.length === 0) {
    console.warn('❌ 所有传播节点都无效，终止动画播放');
    return;
  }

for (const step of spreadSequence) {
  if (this._shouldStopSpread) {
    console.log('🛑 用户中断传播流程');
    break;
  }

  const { node } = step;

  // ✅ 通知拓扑地图（无论三维模型是否存在）
  this.sendToMap({ type: 'spread-step', nodeId: String(node) });

  // 尝试获取三维模型对象
  const object = this.findObjectByNodeId(node);
  if (!object) {
    console.warn(`⚠️ 未找到 node=${node} 的模型对象，仅拓扑显示`);
    await delay(200 / this.spreadSpeed);
    continue;
  }

  const worldPos = new THREE.Vector3();
  object.getWorldPosition(worldPos);
  if (worldPos.length() < 1e-2 || worldPos.y > 50) {
    console.warn(`⚠️ node=${node} 位置无效，仅拓扑显示`);
    await delay(200 / this.spreadSpeed);
    continue;
  }


    // 创建扩散圈
    const ringGeometry = new THREE.RingGeometry(5, 6, 32);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6600,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.copy(worldPos);
    ring.rotation.x = -Math.PI / 2;
    ring.renderOrder = 998;
    this.scene.add(ring);

    let t = 0;
    const ringInterval = setInterval(() => {
      t += 0.04;
      const scale = 10 + t * 60;
      ring.scale.set(scale, scale, 1);
      ring.material.opacity = 0.5 - t * 0.5;
      if (t >= 1.0) {
        this.scene.remove(ring);
        clearInterval(ringInterval);
      }
    }, 33);

    // 创建红色球体（带闪烁计数）
    if (!this._activeSprites.has(node)) {
      if (worldPos.length() < 1e-2) {
        console.warn(`⚠️ node=${node} 的 worldPos 在原点附近，跳过创建球体`);
      } else {
        const geometry = new THREE.SphereGeometry(8, 16, 16);
        const material = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          transparent: true,
          opacity: 0.6,
          depthWrite: false,
        });
        const sphere = new THREE.Mesh(geometry, material);
        sphere.scale.set(1, 0.2, 1);
        sphere.rotation.x = -Math.PI / 2;
        sphere.position.copy(worldPos);
        sphere.renderOrder = 999;

        // ✅ 初始化闪烁计数
        sphere.userData.pulseCount = 0;
        sphere.userData.lastWasMax = false;
        sphere.userData.done = false;

        this.scene.add(sphere);
        this._activeSprites.set(node, sphere);
        console.log(`✅ 添加球体 node=${node}，position=`, sphere.position.toArray());
      }
    }

    const stepDelay = 200 / this.spreadSpeed;
    await delay(stepDelay);
  }
},

async handleSpreadToggle() {
  if (this.isSpreading) {
    this._shouldStopSpread = true;
    this.spreadAbortFlag = true;
    this.isSpreading = false;
    this.clearSpreadGraphics();
    return;
  }

  // ✅ 提取纯数字编号
  const pureNodeId = this.extractNodeId(this.spreadEventNode);
  if (!pureNodeId || !this.spreadEventType) {
    alert('Please enter a node ID and select an event type.');
    return;
  }

  this.showGif = false;
  this.spreadResultSequence = [];
  this.gifUrl = '';
  this.spreadAbortFlag = false;
  this.isSpreading = true;

  try {
    const res = await axios.post('https://bridgetunnelgroup.site/api/wuhan/traffic/spread', {
      node: pureNodeId,  // ✅ 使用提取后的纯编号
      type: this.spreadEventType,
    });

    if (res.data.success) {
      this.spreadResultSequence = res.data.data.spread_sequence;
      this.gifUrl = 'https://bridgetunnelgroup.site/' + res.data.data.gif_path.replace(/^\//, '');

      // ✅ 播放 Three.js 动画（支持中断）
      this.playSpreadAnimation(this.spreadResultSequence);

      // 轮询 spread_sequence.json 获取 gif_path
      const checkGifReady = async () => {
        const maxRetries = 10;
        for (let i = 0; i < maxRetries; i++) {
          try {
            const jsonRes = await axios.get('/scr/data_wuhan/output/spread_sequence.json', {
              params: { t: Date.now() } // 避免缓存
            });

            if (jsonRes.data.gif_path) {
              this.gifUrl = jsonRes.data.gif_path + '?t=' + Date.now(); // 防缓存
              await new Promise(r => setTimeout(r, 200)); // 给一点延迟避免图没刷新
              
              this.showGif = true;
              console.log('🎬 GIF 动画已生成');
              return;
            }
          } catch (err) {
            console.log(`第 ${i + 1} 次轮询 GIF 路径失败，等待 5 秒...`);
          }
          await new Promise((r) => setTimeout(r, 5000));
        }
        alert('Propagation animation generation timed out. Please refresh the page and try again.');
      };

      await checkGifReady();
    } else {
      alert('Simulation failed: ' + res.data.message);
    }
  } catch (err) {
    console.error('请求出错:', err);
    alert('Network error or backend service failure.');
  } 
},
clearSpreadGraphics() {
    this._spriteAnimationActive = false;   // ← 添加这一行

    if (this._activeSprites) {
      this._activeSprites.forEach(s => this.scene.remove(s));
      this._activeSprites.clear();
    }
    if (this._activeRings) {
      this._activeRings.forEach(r => this.scene.remove(r));
      this._activeRings.clear();
    }
    if (this._activeSpheres) {
      this._activeSpheres.forEach(s => this.scene.remove(s));
      this._activeSpheres.clear();
    }
    //this._activeSprites = null;
    //this._activeRings = null;
    this._spriteAnimationStarted = false;
    this.sendToMap({ type: 'spread-clear' });
},

    makeObjectsBlinkByNode(nodeId) {
  if (!nodeId || !this.scene) return;

  // 清除之前的闪烁
  if (this.blinkingInterval) clearInterval(this.blinkingInterval);
  if (this.blinkingTimeout) clearTimeout(this.blinkingTimeout);

  const targetMeshes = [];

  // 收集所有 node 相同的 mesh
  this.scene.traverse(child => {
    if (child.isMesh && child.userData.node === nodeId) {
      const material = Array.isArray(child.material) ? child.material[0] : child.material;
      targetMeshes.push({
        mesh: child,
        material: material,
        originalColor: material.color.clone()
      });
    }
  });

  if (targetMeshes.length === 0) return;

  let visible = true;

  // 启动闪烁
  this.blinkingInterval = setInterval(() => {
    visible = !visible;
    targetMeshes.forEach(({ material, originalColor }) => {
      material.color.set(visible ? 0xff0000 : originalColor);
    });
  }, 300);

  // 5 秒后停止
  this.blinkingTimeout = setTimeout(() => {
    clearInterval(this.blinkingInterval);
    targetMeshes.forEach(({ material, originalColor }) => {
      material.color.set(originalColor);
    });
    this.blinkingInterval = null;
    this.blinkingTimeout = null;
  }, 5000);
},

    toggleObjectsByNodeVisibility() {
    if (!this.selectedObject || !this.selectedObject.userData.node) return;

    const targetNode = this.selectedObject.userData.node;
    const isCurrentlyVisible = !(this.objectVisibilityMap[targetNode] ?? true); // 默认是 true（可见）

    // 遍历场景中所有对象，设置相同 node 的对象 visible
    this.scene.traverse(obj => {
      if (obj.isMesh && obj.userData.node === targetNode) {
        obj.visible = isCurrentlyVisible;
      }
    });

    // 更新可见性记录
    this.$set(this.objectVisibilityMap, targetNode, isCurrentlyVisible);
  },
    goHome() {
      this.resetAllObjectColors();
  // 若你使用 Vue Router：
  this.$router.push('/roadnet');
  // 若你没有用 Vue Router，而是静态页面跳转：
  // window.location.href = '/';
},
    checkCameraMovement() {
    if (this.debugObject && this.camera.position.distanceTo(this.cameraLastPos) > 50) {
      this.debugObject = null;
    }
    this.cameraLastPos.copy(this.camera.position);
  },
    // 简单节流函数（限制执行频率）
throttle(func, limit = 100) {
  let lastCall = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (typeof func === 'function' && now - lastCall >= limit) {
      lastCall = now;
      func.apply(this, args);
    }
  };
},

// 简单防抖函数（延迟执行，避免频繁触发）
debounce(func, delay = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    if (typeof func === 'function') {
      timer = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    }
  };
},
getIntersectedObject(event, maxDistance = Infinity) {
  const rect = this.$refs.threeCanvas.getBoundingClientRect();
  this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  this.raycaster.setFromCamera(this.mouse, this.camera);

  let closestObj = null;
  let closestDistance = Infinity;

  for (const obj of this.simplifiedObjects) {
    if (!obj.visible) continue;
    if (this.excludedNames.some(namePart => obj.name.includes(namePart))) continue;

    const box = obj.userData.boundingBox?.clone(); // clone 以便膨胀
    if (!box || box.isEmpty()) continue;

    // 根据距离动态膨胀点击范围（越远越容易选中）
    const distanceToCam = this.camera.position.distanceTo(obj.position);
    const expandFactor = Math.min(1.0, distanceToCam / 200); 
    box.expandByScalar(0.2 * expandFactor);

    const intersectionPoint = new THREE.Vector3();
    if (this.raycaster.ray.intersectBox(box, intersectionPoint)) {
      const distance = this.camera.position.distanceTo(intersectionPoint);
      if (distance < closestDistance && distance < maxDistance) {
        closestDistance = distance;
        closestObj = obj;
      }
    }
  }

  return closestObj;
},
    onPanelMouseDown(e) {
    this.isDraggingPanel = true;
    this.dragOffset = {
      x: e.clientX - this.debugPanelPosition.x,
      y: e.clientY - this.debugPanelPosition.y,
    };
    document.addEventListener('mousemove', this.onPanelMouseMove);
    document.addEventListener('mouseup', this.onPanelMouseUp);
  },
  onPanelMouseMove(e) {
    if (this.isDraggingPanel) {
      this.debugPanelPosition = {
        x: e.clientX - this.dragOffset.x,
        y: e.clientY - this.dragOffset.y,
      };
    }
  },
  onPanelMouseUp() {
    this.isDraggingPanel = false;
    document.removeEventListener('mousemove', this.onPanelMouseMove);
    document.removeEventListener('mouseup', this.onPanelMouseUp);
  },
  updateLayerVisibility() {
    this.simplifiedObjects.forEach(obj => {
      for (const [layer, keywords] of Object.entries(this.layerKeywordMap)) {
        if (keywords.some(keyword => obj.name.includes(keyword))) {
         obj.visible = this.layerVisibility[layer];
        break;
        }
      }
    });
  }, 

    resetToTopView() {
      this.camera.position.set(31136.99, 285535.36, 1945.34);
    this.camera.lookAt(new THREE.Vector3(31136.99, 535.36, 1945.05));
    if (this.controls) {
      this.controls.target.set(31136.99, 535.36, 1945.05);
      this.controls.update();
    }
  },
  resetToSideView() {
    this.camera.position.set(-39322.36091824727, 16988.720887705604, 57445.01734023128);
    this.camera.lookAt(new THREE.Vector3(-163.68, -7269.55, 10363.21));
    if (this.controls) {
      this.controls.target.set(-17567.463832695557, 136.7351491752899, 18417.48751002929);
      this.controls.update();
    }
  },
  
        toggleExcludedVisibility() {
    this.hideExcluded = !this.hideExcluded;
  
    this.simplifiedObjects.forEach(obj => {
        if (this.excludedNames.some(namePart => obj.name.includes(namePart))){
        obj.visible = !this.hideExcluded;
      }
    });
  
    // 找到地面对象并移动
    const ground = this.scene.getObjectByName('ground');
    if (ground) {
      const newY = this.hideExcluded
        ? this.groundOriginalY - 20  // 向下移动 20
        : this.groundOriginalY;     // 复位到原始位置
  
      // 使用 gsap 动画平滑移动
      gsap.to(ground.position, {
        y: newY,
        duration: 0.6,
        ease: 'power2.out',
      });
    }
  }
  ,
  
focusOnObject(name) {
  // 找出所有名称中包含该桥梁名的 mesh
  const meshes = this.simplifiedObjects.filter(obj => obj.name && obj.name.includes(name));
  if (meshes.length === 0) {
    console.warn('未找到对应对象:', name);
    return;
  }

  // 计算所有匹配 mesh 的合并包围盒
  const box = new THREE.Box3();
  meshes.forEach(m => box.expandByObject(m));
  const center = new THREE.Vector3();
  box.getCenter(center);

  const offset = new THREE.Vector3(0, 1000, 2500);
  const newPos = center.clone().add(offset);

  // ⚠️ 原来这行用的是 targetObj，改为取第一个匹配的 mesh
  const selectedNode = meshes[0]?.userData?.node;
  this.makeObjectsBlinkByNode(selectedNode);

  // 动画飞过去
  gsap.to(this.camera.position, {
    duration: 1.2,
    x: newPos.x,
    y: newPos.y,
    z: newPos.z,
    ease: 'power2.out',
    onUpdate: () => {
      this.camera.lookAt(center);
    }
  });

  gsap.to(this.controls.target, {
    duration: 1.2,
    x: center.x,
    y: center.y,
    z: center.z,
    ease: 'power2.out',
    onUpdate: () => {
      this.controls.update();
    }
  });
},
  
  async loadGlobalRanks() {
  try {
    const data = await this.fetchImportanceData();
    const map = {};
    data.forEach(item => {
      map[item.name] = {
        node: item.node,            // 节点编号
        congestion: item.congestion // rank
      };
    });
    this.globalRankMap = map; // { name: { node, congestion } }
this.buildAllNodeOptions();
    console.log('✅ 全局 rankMap 已加载（包含 node）:', this.globalRankMap);
  } catch (err) {
    console.error('加载全局 rank 失败:', err);
  }
},
  /** ========== 运行模拟核心方法 ========== */
async runSimulation() {
const pureNodeId = this.extractNodeId(this.eventNode);
  try {
    // 发送新参数
    await axios.post('https://bridgetunnelgroup.site/api/wuhan/traffic-v2/run', {
      targetWeekday: this.targetWeekday,
      targetHour: this.targetHour,
      eventNode: pureNodeId || null,
      eventType: this.eventType || null,
    });
    
    // 拉取最新完整数据（需要处理返回的时间线）
    const latestData = await this.fetchImportanceData();
    this.simulationResult = latestData;
    
    // 更新模型颜色
    this.refreshAllObjectColors();
    this.showSimulationChart = true;
    
    // 同步更新动画模型速度
    if (this.model2Visible) {
      await this.updateBridgeAnimationsByFlow();
    }
    // 更新流量柱和数字精灵
    if (this.flowSpritesVisible) {
      await this.updateAllNodesNumbers();
    }
    // 刷新全局 rank
    await this.loadGlobalRanks();
    
    // 渲染图表
    this.$nextTick(() => {
      setTimeout(() => {
        const chartDom = this.$refs.simulateBarChart;
        if (chartDom) {
          this.renderSimulationChart();
        }
      }, 50);
    });
    
    console.log('模拟完成', this.simulationResult);
  } catch (err) {
    console.error('模拟失败:', err);
    alert('Simulation failed. Please check the console.');
  }
},



// 渲染图表（按 congestion 排序或流量排序均可，这里还是按流量前20展示）
renderSimulationChart() {
    const chartDom = this.$refs.simulateBarChart;
    if (!chartDom || chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
      console.warn('图表容器未就绪，延迟重试...');
      setTimeout(() => this.renderSimulationChart(), 100);
      return;
    }

    // 销毁旧实例避免重复初始化
    if (echarts.getInstanceByDom(chartDom)) {
      echarts.dispose(chartDom);
    }
    const myChart = echarts.init(chartDom);
    
    // ✅ 使用全部模拟结果，不截取
    const allData = [...this.simulationResult]
      .filter(item => item.flow > 1e-6);
    

    const option = {
      backgroundColor: '#1e1e2f', //  图表背景色
      title: {
        text: 'Bridge–Tunnel Network Traffic Flow Distribution',
        left: 'center',
        textStyle: { color: '#ffffff' } // ✅ 标题颜色
      },
      tooltip: { trigger: 'item' },
      grid: {
      left: 10,
      right: 10,
      top: 50,
      bottom: 30, // 增加底部间距，避免被滚动条和文字遮挡
      containLabel: true
      },
      dataZoom: [
      {
        type: 'slider',  // 底部滑动条
        show: true,
        xAxisIndex: 0,
        start: 0,        // 默认展示前 20%
        end: 20          // 可调节初始展示比例
      },
      {
        type: 'inside',  // 鼠标滚轮或触摸板也可滑动
        xAxisIndex: 0
      }
    ],
      xAxis: {
        type: 'category',
        data: allData.map(i => i.name || `Node ${Number(i.node) + 1}`),
        axisLabel: { color: '#dddddd', rotate: 45, margin: 10 },
        axisLine: { lineStyle: { color: '#888888' } },
        axisTick: { lineStyle: { color: '#888888' } }
      },
      yAxis: { type: 'value', 
      name: 'Traffic Flow (veh/h)',
      nameTextStyle: { color: '#dddddd' },
      axisLabel: { color: '#dddddd' },
      axisLine: { lineStyle: { color: '#888888' } },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } } // 网格线浅灰
      },
      series: [{
        type: 'bar',
        data: allData.map(i => ({
          value: i.flow,
          itemStyle: { color: `#${this.getColorByRank(i.congestion).toString(16).padStart(6, '0')}` } // 直接使用后端的congestion
        }))
      }]
    };

    myChart.setOption(option);
  },


  // 获取 rank 颜色（直接使用 congestion）
  getRankColor(rank) {
    return this.rankColors[rank] || '#ffffff';
  },

  // 刷新颜色逻辑：直接根据 congestion 赋色
  async refreshAllObjectColors() {
    try {
      // 获取合并后的节点数据（包含 node、name、congestion）
    const data = await this.fetchImportanceData(); 
    if (!Array.isArray(data)) {
      console.error('❌ 获取的重要性数据不是数组:', data);
      return;
    }

      // 遍历场景中的简化模型对象
    this.simplifiedObjects.forEach(obj => {
      // 模糊匹配对象名
      const match = data.find(item => obj.name.includes(item.name));
      if (match) {
        const congestion = match.congestion;
        obj.material.color.setHex(this.getColorByRank(congestion));
      }
    });

      console.log('✅ 模型颜色已刷新');
    } catch (err) {
      console.error('❌ 刷新颜色失败:', err);
    }
  },

  resetAllObjectColors() {
    this.simplifiedObjects.forEach(obj => {
      if (obj && obj.material && obj.userData.originalColor) {
        obj.material.color.copy(obj.userData.originalColor);
      }
    });
    console.log('✅ 所有模型颜色已重置为默认色');
  },

  async submitAllRanks() {
    try {
      const updates = Object.entries(this.globalRankMap).map(([name, rank]) => ({ name, rank }));
      await api.post('/importance/ranking/bulk', updates);
      alert('Batch update completed successfully.');
      this.refreshAllObjectColors(); // 刷新模型颜色
    } catch (err) {
      console.error('批量提交失败', err);
      alert('Update failed.');
    }
  },
  
      toggleRankFilter(rank) {
        this.selectedRankFilter = (this.selectedRankFilter === rank) ? null : rank;
  
        // 遍历所有对象，根据 rank 决定是否可见
        this.simplifiedObjects.forEach(obj => {
        const rankOfObj = this.getRankFromColor(obj.material.color.getHex());
        obj.visible = (this.selectedRankFilter === null || rankOfObj === this.selectedRankFilter);
        });
      },
  
      getRankFromColor(hexValue) {
        for (const [rank, hex] of Object.entries(this.rankColors)) {
          const colorHex = new THREE.Color(hex).getHex(); // 转为数字 hex
          if (hexValue === colorHex) {
            return Number(rank);
          }
        }
        return null;
      },
  
      logSelectedObject() {
        console.log('当前选中对象完整结构:', this.selectedObject);
        },
  
        updateCurrentColorPreview() {
          if (!this.selectedObject || !this.selectedObject.material) {
            this.currentObjectColorHex = '#cccccc';
            return;
        }
  
        const color = this.selectedObject.material.color;
        this.currentObjectColorHex = `#${color.getHexString()}`;
      },
      async fetchCurrentRank(name) {
  this.isRankLoading = true;
  try {
    // 调用新接口获取最新模拟结果
    const { data } = await api.get('/wuhan/traffic-v2/latest');

    // 按名称精确匹配
    const item = data.find(i => i.name === name);

    if (item && typeof item.congestion === 'number') {
      // 使用 congestion 作为 rank
      this.editRank = item.congestion;
    } else {
      this.editRank = null;
    }
  } catch (err) {
    console.error('获取 rank 失败:', err);
    this.editRank = null;
  } finally {
    this.isRankLoading = false;
  }
},

  
      async updateRank() {
       if (!this.selectedObject || !this.editRank) return;
  
        const name = this.selectedObject.name;
        const rank = this.editRank;
  
        try {
         await api.post('/importance/ranking', { name, rank });
  
          // 更新颜色
          this.selectedObject.material.color.set(this.rankColors[rank] || '#cccccc');
  
          alert('Congestion level saved.');
        } catch (err) {
          console.error('更新失败:', err);
          alert('Update failed. Please check the console.');
        }
      },
      
async fetchImportanceData() {
  try {
    const [mappingRes, congestionRes] = await Promise.all([
      api.get('/wuhan/node-importance/ranking/full'),
      api.get('/wuhan/traffic-v2/latest')
    ]);
    const mappingData = mappingRes.data || [];
    const congestionData = congestionRes.data || [];  // 直接就是扁平节点数组

    const merged = congestionData.map(congItem => {
      const mapItem = mappingData.find(m => m.node === congItem.node);
      return {
        node: congItem.node,
        name: mapItem ? mapItem.name : `Node-${congItem.node}`,
        congestion: congItem.congestion,
        speed: congItem.speed,
        flow: congItem.flow,
      };
    });

    this.updateLatestNodeDataMap(merged);
    if (this.selectedObject) {
      this.updateDebugPanelBySelectedObject(this.selectedObject);
    }
    return merged;
  } catch (err) {
    console.error('获取节点信息失败：', err);
    return [];
  }
},


  
  
      getColorByRank(congestion) {
        const colorMap = {
          4: 0xff0000, // 红色
          3: 0xffa500, // 橙色
          2: 0xffff00, // 黄色
          1: 0x00ff00, // 绿色
          0: 0x1e90ff, // 蓝色
        };
        return colorMap[congestion] || 0xcccccc; // 默认灰色
      },
      addGround() {
    // 白色地面（尽量大）
    const groundGeometry = new THREE.PlaneGeometry(350000, 350000); //  非无限但足够大
    const groundMaterial = new THREE.MeshStandardMaterial({
      //color: 0xE4EBF2,
      //color: 0xFFFFFF,//淡白色
      //color: 0xe6ecf2,//淡灰蓝色
      //color: 0xf5f5f5,//淡米白色
      color: 0xf5f5f5,
      roughness: 0.95,
      metalness: 0.1,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
  
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -30; // 稍微降低地面
    ground.receiveShadow = true;
    ground.userData.ignoreRaycast = true;
    ground.renderOrder = -1;
    ground.name = 'ground';
    this.scene.add(ground);
  
    // 网格辅助线
    //const grid = new THREE.GridHelper(100000, 1000, 0xe0e0e0, 0xe0e0e0);
    //grid.position.y = -19.9; // 比地面略高，防止遮挡
    //this.scene.add(grid);
  }
  ,
      addSkybox() {
        const loader = new THREE.TextureLoader();
        const texture = loader.load('textures/sky.png'); // 替换为实际的等距矩形贴图路径
        texture.mapping = THREE.EquirectangularReflectionMapping; // 设置为等距矩形贴图映射
  
        // ✅ 使用场景背景设置，而不是创建球体
      this.scene.background = texture;
  
        // ✅ 可选：同时设置环境贴图用于反射
      this.scene.environment = texture;
      
      },
      printModelStructure(object, depth = 0) {
        const indent = '  '.repeat(depth);
        console.log(`${indent}${object.name || '(Unnamed)'} - ${object.type}`);
        if (object.children) {
          object.children.forEach((child) => this.printModelStructure(child, depth + 1));
        }
      },
      updateInfo() {
        if (this.selectedObject) {
          console.log('Updated Object Info:', this.selectedObject.userData);
          alert(`Object Info Updated:
        ID: ${this.selectedObject.userData.id}
        Type: ${this.selectedObject.userData.type}
        Strain: ${this.selectedObject.userData.strain}`);
        } else {
          alert('No object selected to update.');
        }
      },
      initChart() {
        const chartDom = this.$refs.pieChartContainer;
        const myChart = echarts.init(chartDom);
        
        const option = {
          backgroundColor: 'rgba(192,192,192,0.6)',
          aria: {
              enabled: true,
              decal: {
                  show: true
              }
          },
          legend: {},
          tooltip: {
            trigger: 'axis',
            showContent: false
          },
          dataset: {
            source: [
              ['product', '2020', '2021', '2022', '2023', '2024', '2025'],
              ...this.chartData.map(item => [item.product, item['2020'], item['2021'], item['2022'], item['2023'], item['2024'], item['2025']])
            ]
          },
          xAxis: { type: 'category' },
          yAxis: { gridIndex: 0 },
          grid: { top: '55%' },
          series: [
            { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
            { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
            { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
            { type: 'line', smooth: true, seriesLayoutBy: 'row', emphasis: { focus: 'series' } },
            {
              type: 'pie',
              id: 'pie',
              radius: '30%',
              center: ['50%', '25%'],
              emphasis: { focus: 'self' },
              label: { formatter: '{b}: {@2012} ({d}%)' },
              encode: { itemName: 'product', value: '2012', tooltip: '2012' }
            }
          ]
        };
  
        myChart.setOption(option);
        // 更新饼图内容，当鼠标移动到折线图上时触发
        myChart.on('updateAxisPointer', (event) => {
  if (!event || !Array.isArray(event.axesInfo) || !event.axesInfo.length) {
    return; // ⭐ 关键
  }

  const xAxisInfo = event.axesInfo[0];
  if (!xAxisInfo) return;

  const dimension = xAxisInfo.value + 1;
  myChart.setOption({
    series: {
      id: 'pie',
      label: {
        formatter: `{b}: {@[${dimension}]} ({d}%)`
      },
      encode: {
        value: dimension,
        tooltip: dimension
      }
    }
  });
});
      },
      initThree() {
        // 初始化场景
        this.scene = new THREE.Scene();
        //this.scene.background = new THREE.Color("#ECFAFF");
        this.scene.background = new THREE.Color("#B0D8FF"); // 介于天蓝和白之间
  
        // 设置渲染器
        this.renderer = new THREE.WebGLRenderer({ antialias: true, logarithmicDepthBuffer: true });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.outputEncoding = THREE.sRGBEncoding;
        this.renderer.setClearColor(0xf4f4f4);//柔和白色背景
        this.$refs.threeCanvas.appendChild(this.renderer.domElement);
  
        // 初始化相机
        const width = window.innerWidth;
        const height = window.innerHeight;
        this.camera = new THREE.PerspectiveCamera(30, width / height, 1, 10000000);
        //this.camera.position.set(-21946, 1612, 5319);
        //this.camera.lookAt(0, -145, 300);
        this.camera.position.set(-39322.36091824727, 16988.720887705604, 57445.01734023128);
        this.camera.lookAt(-163.68, -7269.55, 10363.21);
        //this.camera.position.set(-21946, 1612, 5319);
        //this.camera.lookAt(0, -145, 300);
  
        // 初始化轨道控制器
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.target.set(-17567.463832695557, 136.7351491752899, 18417.48751002929);
        this.controls.update();
        this.controls.enableDamping = false;
 
  
        // 添加光源
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
        this.scene.add(ambientLight);

        // 半球光：增强阴影面光照
        const hemiLight = new THREE.HemisphereLight(0xffffff, 0x999999, 1.2);
        hemiLight.position.set(0, 500, 0);
        this.scene.add(hemiLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
        directionalLight.position.set(0, 2000, 3000);
        this.scene.add(directionalLight);
        
        const backLight = new THREE.DirectionalLight(0xffffff, 3); // 可调节强度
        backLight.position.set(0, -2000, -3000); // 与主光源位置相反
        this.scene.add(backLight);
        
        // 加载模型
        this.loadModel();
        //this.addSkybox();
        this.addGround();
  
        // 添加事件监听
        this.$refs.threeCanvas.addEventListener('click', this.onMouseClick);
  
        // 开始渲染循环
        this.animate();
      },
  
      async loadModel() {
        console.log('Loading model via global loader...');
        try {
          // 如果缓存还没完成加载，等待全局Promise
          //await modelLoader.loadModel('wh2', '/models/wh2.glb');
          //await modelLoader.loadModel('sh13', '/models/sh13.glb');
          //await modelLoader.loadModel('SZ', '/models/SZ4.glb');
          await modelLoader.loadModel('wh19', '/models/wh19.glb');
          await modelLoader.loadModel('wh20-p', '/models/wh20-p.glb');
          //拿取克隆副本
          //const model = modelLoader.getModelClone('sh13');
          //await modelLoader.loadModel('wh', '/models/wh1.glb');
          const model = modelLoader.getModelClone('wh19');
          const model2 = modelLoader.getModelClone('wh20-p');
          //const model = modelLoader.getModelClone('wh2')
          const animations = modelLoader.getAnimations('wh20-p');
          console.log('总动画剪辑数:', animations.length);
animations.forEach((clip, index) => {
  console.log(`--- 剪辑 ${index}: ${clip.name} ---`);
  clip.tracks.forEach(track => {
    // 只打印包含“TXZ”、“QS”、“YWZ”的轨道，快速过滤
    if (/TXZ|QS|YWZ/.test(track.name)) {
      console.log('  ', track.name);
    }
  });
});
          

if (animations && animations.length > 0) {

  const bridgeConfigs = [
  // 旧桥，使用剪辑 0
  { prefix: 'WHCJ1_', objectName: '武汉长江大桥动画', defaultSpeed: 0.001, clipIndex: 0 },
  { prefix: 'WHCJ2_', objectName: '武汉长江二桥动画', defaultSpeed: 0.02, clipIndex: 0 },
  { prefix: 'YSG_', objectName: '杨泗港长江大桥动画', defaultSpeed: 0.05, clipIndex: 0 },
  { prefix: 'YL_', objectName: '阳逻大桥动画', defaultSpeed: 0.9, clipIndex: 0 },
  { prefix: 'BSZ_', objectName: '白沙洲大桥动画', defaultSpeed: 0.9, clipIndex: 0 },
  { prefix: 'EQ_', objectName: '二七长江大桥动画', defaultSpeed: 0.9, clipIndex: 0 },

  // 新桥，指定正确的剪辑索引
  { prefix: 'TXZ_', objectName: '天兴洲动画', defaultSpeed: 0.9, clipIndex: 1 },   // 剪辑 1
  { prefix: 'QS_', objectName: '青山动画', defaultSpeed: 0.9, clipIndex: 2 },      // 剪辑 2
  { prefix: 'YWZ_', objectName: '鹦鹉洲动画', defaultSpeed: 0.9, clipIndex: 1 },   // 剪辑 1
  ];

  this.bridgeMixers = [];

bridgeConfigs.forEach(({ prefix, objectName, defaultSpeed, clipIndex = 0 }) => {
  const bridgeRoot = model2.getObjectByName(objectName);
  if (!bridgeRoot) {
    console.warn(`⚠️ 未找到桥根对象: ${objectName}`);
    return;
  }

  // ✅ 从指定的动画剪辑中获取轨道
  const sourceClip = animations[clipIndex];
  if (!sourceClip) {
    console.warn(`⚠️ 桥 ${objectName} 指定的动画剪辑索引 ${clipIndex} 不存在`);
    return;
  }

  const tracks = sourceClip.tracks.filter(t => t.name.startsWith(prefix));
  if (tracks.length === 0) {
    console.warn(`⚠️ 桥 ${objectName} 没有匹配轨道`);
    return;
  }

  const subClip = new THREE.AnimationClip(objectName, sourceClip.duration, tracks);
  const mixer = new THREE.AnimationMixer(bridgeRoot);
  const action = mixer.clipAction(subClip);
  action.setLoop(THREE.LoopRepeat);
  action.timeScale = defaultSpeed;
  action.play();

  this.bridgeDefaultSpeeds[objectName] = defaultSpeed;
  this.bridgeMixers.push({ name: objectName, mixer, root: bridgeRoot });

  console.log(`✅ 已加载 ${objectName}，速度 ${defaultSpeed}x`);
});

  console.log(`🎬 共加载 ${this.bridgeMixers.length} 个桥动画`);
} else {
            console.log('⚠️ 模型无动画');
          }
          this.model2 = model2;     

          model2.visible = false;
          model2.scale.set(0.3, 0.3, 0.3);
          model.scale.set(0.3, 0.3, 0.3);
          model.updateMatrixWorld(true);
          model2.updateMatrixWorld(true);//  确保缩放生效

          // importanceMap 是数组
          const importanceMap = await this.fetchImportanceData();

          // 定义一个通用处理函数，用于遍历模型内的所有 Mesh 并设置属性
    model.traverse((child) => {
      if (child.isMesh) {
        // 统一名称继承逻辑（可选）
        if (!child.name && child.parent && child.parent.name) {
          child.name = child.parent.name;
        }

        child.userData.boundingBox = new THREE.Box3().setFromObject(child).clone();// 缓存包围盒
        console.assert(!child.userData.boundingBox.isEmpty(), `包围盒为空: ${child.name}`);

        child.castShadow = true;
        child.receiveShadow = true;

        // ✅ 模糊匹配：child.name 包含数据库 name 字段
        const match = importanceMap.find(item => item.name && child.name.includes(item.name));

        if (match && typeof match.node === 'number') {
          // ✅ 保存原始颜色（只保存一次）
          if (!child.userData.originalColor) {
            child.userData.originalColor = child.material.color.clone();
          }
          // ✅ 直接设置颜色，不 clone 材质
          //const targetColor = this.getColorByRank(match.congestion);
          //const currentColorHex = child.material.color.getHex();

          //if (currentColorHex !== targetColor) {
            //child.material.color.setHex(targetColor);
          //}


          // ✅ 写入完整信息到 userData
          Object.assign(child.userData, {
            node: match.node,
            rank: match.congestion,  // 保留 rank 别名
            congestion: match.congestion,
            speed: match.speed,
            flow: match.flow,
          });

          console.log(`✅ 已为 ${child.name} 设置 node=${match.node}, rank=${match.rank}`);
        } else {
          console.warn(`❌ 跳过未匹配对象: ${child.name}`);
        }

        // ✅ 收录所有 mesh
        this.simplifiedObjects.push(child);
      }
    });

          this.scene.add(model);
          this.scene.add(model2);
          console.log('Loaded Model:', model);
          console.log('Loaded Model2:', model2);
          this.printModelStructure(model);
          this.printModelStructure(model2);
          this.updateLayerVisibility();
          // 初始化车辆检测（必须在模型添加到场景后，以便获取世界坐标）
          this.initVehicleDetection(model2);

          console.log('Model loaded successfully.');
        } catch (error) {
          console.error('Error loading model:', error);
        } finally {
          this.modelLoading = false;
        }
      },

  
      animate() {
          if (!this.isAnimating) return;
  if (this.viewMode === 'three') {
          // 计算帧间隔时间（秒），限制最大间隔防止跳跃过大
          const now = performance.now();
          const delta = Math.min(1/30, (now - (this.lastTime || now)) / 1000);
          this.lastTime = now;

          // 更新四个桥的动画
          if (this.bridgeMixers) {
            this.bridgeMixers.forEach(({ mixer }) => mixer.update(delta));
          }
          // 车辆高速隐藏逻辑
          if (this.vehicleMeshes.length > 0 && delta > 0 && delta < 0.1) {
            this.updateVehicleVisibility(delta);
          }

            const groundY = 10;
            const targetY = this.controls.target.y;
            const distance = this.camera.position.distanceTo(this.controls.target);

            // 计算 camera.y >= groundY 允许的最大极角
            // camera.y = targetY + distance * cos(polar) >= groundY
            // → cos(polar) >= (groundY - targetY) / distance
            const ratio = (groundY - targetY) / distance;
            const clampedRatio = Math.max(-1, Math.min(1, ratio));
            const maxPolar = Math.acos(clampedRatio);

            this.controls.maxPolarAngle = maxPolar;

            // 同时防止平移把目标点拖到地面以下
            if (this.controls.target.y < groundY) {
              this.controls.target.y = groundY;
            }
            
          this.renderer.render(this.scene, this.camera);
          this.checkCameraMoveThrottled();
          this.cameraLastPos.copy(this.camera.position);
          this.controls.update();
          this.updateSpritesScreenSize();

          if (this.skybox) {
            this.skybox.position.copy(this.camera.position);
          }
  }
          requestAnimationFrame(this.animate);
        },
  
      onMouseClick(event) {
  this.debugObject = null;
  const closestObj = this.getIntersectedObject(event);
  if (closestObj && closestObj !== this.selectedObject) {
    this.highlightObject(closestObj);
  }
},
  
highlightObject(object) {
  if (!object || !object.material) return;

  // 恢复上一个选中对象颜色
  if (this.selectedObject && this.selectedObject !== object) {
    this.resetHighlight(this.selectedObject);
  }

  //  应用高亮颜色
  this.applyHighlight(object);

  // 更新当前选中对象引用
  this.selectedObject = object;
},
resetHighlight(object) {
  if (!object || !object.material) return;

  if (object.material.emissive && object.currentHex !== undefined) {
    object.material.emissive.setHex(object.currentHex);
  } else if (object.material.color && object.currentColor !== undefined) {
    object.material.color.setHex(object.currentColor);
  }
},

applyHighlight(object) {
  if (!object || !object.material) return;

  if (object.material.emissive) {
    object.currentHex = object.material.emissive.getHex();
    object.material.emissive.setHex(0xff0000);
  } else if (object.material.color) {
    object.currentColor = object.material.color.getHex();
    object.material.color.setHex(0xff0000);
  }
},
onRightClick(event) {
  event.preventDefault();

  const closestObj = this.getIntersectedObject(event, Infinity);

  if (closestObj) {
    // 右键也同步更新 selectedObject
    if (closestObj !== this.selectedObject) {
      this.highlightObject(closestObj);
    } else {
      this.selectedObject = closestObj;
    }

    // 显示调试面板
    this.debugObject = closestObj;

    
    if (typeof this.updateDebugPanelBySelectedObject === 'function') {
      this.updateDebugPanelBySelectedObject(closestObj);
    }

    // 更新颜色显示
    if (closestObj.currentColor !== undefined) {
      this.currentObjectColorHex =
        '#' + closestObj.currentColor.toString(16).padStart(6, '0');
    } else if (closestObj.material && closestObj.material.color) {
      this.currentObjectColorHex =
        '#' + closestObj.material.color.getHexString();
    }

    //更新 rank 编辑值
    this.editRank = closestObj.userData?.rank ?? null;

    //面板位置
    this.debugPanelPosition = {
      x: event.clientX,
      y: event.clientY
    };

    console.log('右键选中对象:', closestObj.name, closestObj.userData);
  }
},

  
resetModel() {
  if (this.selectedObject) {
    const material = this.selectedObject.material;

    if (material) {
      // 如果有 emissive 属性，恢复高亮前的颜色
      if ('emissive' in material && material.emissive) {
        material.emissive.setHex(this.selectedObject.currentHex || 0x000000);
      } 
      // 否则恢复原始颜色
      else if ('color' in material && material.color) {
        material.color.setHex(this.selectedObject.currentColor || 0xffffff);
      }
    }

    this.selectedObject = null;
    this.selectedObjectInfo = '';
  }
},

    },
    watch: {
    showRankPanel(val) {
      if (val) this.loadGlobalRanks();
    }
    },
    async mounted() {
        this._onGlobalClick = this.onGlobalClick.bind(this);
  document.addEventListener('click', this._onGlobalClick);
      // 按 P 键打印相机位置和目标点
this._onKeyDown = (e) => {
  if (e.key === 'p' || e.key === 'P') {
    console.log('camera.position =', this.camera.position.toArray());
    console.log('controls.target =', this.controls.target.toArray());
  }
};
window.addEventListener('keydown', this._onKeyDown);
      this.initThree();
      this.initChart();
      this.resizeRendererDebounced = this.debounce(this.resizeRenderer, 300);
      window.addEventListener('resize', this.resizeRendererDebounced);
      //this.refreshAllObjectColors(); // 首次加载
      this.loadGlobalRanks(); 
      this.cameraLastPos.copy(this.camera.position);
      //this.animate(); // 启动渲染循环
      //setInterval(this.refreshAllObjectColors, 600000); // 每 60 秒刷新一次
      this.renderer.domElement.addEventListener('click', this.onMouseClick); // 左键：高亮
      this.renderer.domElement.addEventListener('auxclick', this.handleMiddleClick);
      this.renderer.domElement.addEventListener('contextmenu', this.onRightClick); // 右键：调试面板
      this.renderer.domElement.addEventListener('contextmenu', (e) => e.preventDefault()); // 阻止默认菜单
    },
    beforeDestroy() {
        if (this._onGlobalClick) {
    document.removeEventListener('click', this._onGlobalClick);
    this._onGlobalClick = null;
  }
  // 0. 移除 window 事件
  window.removeEventListener('resize', this.resizeRendererDebounced);

  // 1. 停止动画循环
  this.isAnimating = false;

  // 2. 释放 Three.js 资源
  if (this.scene) {
    this.scene.traverse(child => {
      if (child.isMesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
  }

  // 3. 释放 renderer
  if (this.renderer) {
    this.renderer.dispose();
    this.renderer.forceContextLoss?.();
    this.renderer.domElement = null;
  }

  // 4. 释放 controls
  if (this.controls) {
    this.controls.dispose();
    this.controls = null;
  }

  // 5. 清空对象引用
  this.simplifiedObjects = [];
  this.debugObject = null;
  this.selectedObject = null;
  this.scene = null;
  this.camera = null;

  // 6. 移除 canvas 事件
  const canvas = this.$refs.threeCanvas;
  if (canvas) {
    canvas.removeEventListener('click', this.onMouseClick);
    canvas.removeEventListener('contextmenu', this.onRightClick);
  }

  // 7. ✅ 释放 SHM（监测模块）
  if (this.monitorTimer) {
    clearInterval(this.monitorTimer);
    this.monitorTimer = null;
  }

  if (this.monitorChartInstance) {
    this.monitorChartInstance.dispose();
    this.monitorChartInstance = null;
  }
  if (this.renderer?.domElement) {
    this.renderer.domElement.removeEventListener('auxclick', this.handleMiddleClick);
  }
  console.log('🔻 已释放所有 Three.js + SHM 资源并销毁组件');
  // 移除键盘监听
if (this._onKeyDown) {
  window.removeEventListener('keydown', this._onKeyDown);
  this._onKeyDown = null;
}
}
};
</script>

<style scoped>
/* ✅ 新增：下拉列表滚动条样式 */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: #1f2937;
  border-radius: 2px;
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 2px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.loader-ring {
  border: 6px solid rgba(255, 255, 255, 0.2);
  border-top: 6px solid #00d4ff;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter, .fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}
.btn-primary {
  @apply bg-cyan-500 text-white rounded px-3 py-1 font-bold hover:bg-cyan-400;
}
.btn-secondary {
  @apply bg-gray-700 text-white rounded px-3 py-1 hover:bg-gray-600;
}
.input-box {
  @apply w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1;
}
table th, table td {
  @apply border-b border-gray-600 py-1;
}
.btn-mini {
  @apply text-xs px-2 py-1 border rounded bg-gray-800 hover:bg-gray-600;
}
.btn-active {
  @apply bg-indigo-600 text-white font-bold;
}
</style>